import ReviewActivity, { ReviewActivityProvider } from "./ReviewActivity";
import { normalizeFinding, validateFindingDetails } from "../../components/Inspections/findingFormRules";
import { checklistScopeView } from "./checklistScopeView";
import ObservationsPanel from "../../components/Inspections/ObservationsPanel";
import ChecklistQueryThread from "./ChecklistQueryThread";
import { preserveQueryPosition } from "./preserveQueryPosition";
import { getQueryResponseReview } from "../../helpers/backend_helper";
import ReviewHeaderActions from "./ReviewHeaderActions";
import Discussion from "../../components/Inspections/Discussion";
import EvidenceReferences from "./EvidenceReferences";
import { useUnsavedQuestionNavigation } from "./useUnsavedQuestionNavigation";
import { canContributeToReview } from "./reviewPermissions";
import { usePiersAssistantContext } from "../../components/Assistant/PiersAssistant";
import { getData, postData } from "../../helpers/url-request";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import QueryActionMenu from "./QueryActionMenu";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";
import {
  addInspectionFindingMessage,
  closeInspectionChecklistQuery,
  completeInspectionReviewWorkflowTask,
  createInspectionChecklistDiscussion,
  createInspectionChecklistQuery,
  createInspectionFinding,
  deleteInspectionChecklistQuery,
  deleteInspectionEvidence,
  deleteInspectionFinding,
  downloadInspectionDocument,
  getEngagementDocumentFolders,
  getEngagementDocuments,
  getEngagementDetail,
  getInspectionCycle,
  getInspectionActivities,
  getInspectionChecklist,
  getInspectionChecklistBookmarks,
  getInspectionChecklistDiscussions,
  getDiscussionMentionUsers,
  getInspectionChecklistQueries,
  getInspectionEvidence,
  getChecklistEvidence,
  getInspectionFinding,
  getInspectionFindingConfiguration,
  getInspectionFindings,
  getInspectionGeneratedReports,
  getInvitableUsersList,
  getLoggedInUser,
  reopenInspectionChecklistQuestionForReview,
  respondToInspectionChecklistQuery,
  saveInspectionChecklistResponse,
  saveInspectionChecklistSignOffComment,
  saveInspectionFindingManagementResponse,
  setInspectionChecklistBookmark,
  submitInspectionForApproval,
  transitionInspectionFinding,
  updateInspectionChecklistQuery,
  updateInspectionFinding,
  uploadInspectionChecklistDiscussionAttachment,
  uploadInspectionChecklistQueryAttachment,
  uploadInspectionEvidence,
} from "../../helpers/backend_helper";
import WorkflowResponseDrawer from "../../components/Inspections/WorkflowResponseDrawer";
import FindingEditorModal from "../../components/Inspections/FindingEditorModal";
import FindingsTab from "../../components/Inspections/FindingsTab";
import FindingWorkspaceModal from "../../components/Inspections/FindingWorkspaceModal";
import { Modal, ModalBody } from "../../components/Common/AppOverlay";
import { useContextualHelp } from "../../components/Help/ContextualHelp";
import * as url from "../../helpers/url_helper";
import "./InspectionChecklistReview.scss";
import "./QuestionWorkspace.scss";
import "../../components/Common/StatusQuickFilters.scss";
import { questionAnswerSettings, initialQuestionAnswer } from "./questionAnswer";
import { checklistQuestionGuidance } from "./checklistQuestionGuidance";
import { checklistRecords } from "./checklistRecords";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import { activeMention, updateMentionRanges, readDiscussionMentions } from "./discussionMentions";
import FullScreenPageLayout from "../../components/Common/FullScreenPageLayout";

const responseLabels = { yes: "Yes", no: "No", na: "Not Applicable" };

const emptyQuery = () => ({
  subject: "",
  details: "",
  priority: "Medium",
  assigneeId: "",
  dueDate: "",
  queryType: "Clarification",
  notifyAssigneeByEmail: true,
});
const queryAttachmentExtensions = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".png",
  ".jpg",
  ".jpeg",
];

const statusIcon = (status) =>
  ({
    Answered: "bx-check-circle",
    "In Progress": "bx-circle",
    "Not Started": "bx-circle",
    Flagged: "bx-flag",
    "Has Query": "bx-message-rounded-dots",
  }[status] || "bx-circle");

const questionStatus = (status) => status.toLowerCase().replaceAll(" ", "-");

const queryStage = (status) => {
  const value = String(status || "Open")
    .toLowerCase()
    .replaceAll(" ", "");
  if (value.includes("closed")) return "Resolved";
  if (value.includes("resolved")) return "Resolved";
  return "Open";
};

const queryWorkspaceStatus = (status) => {
  const value = String(status || "")
    .toLowerCase()
    .replaceAll(" ", "");
  if (value.includes("closed")) return "Resolved";
  if (value.includes("resolved")) return "Resolved";
  if (value.includes("awaitingreview")) return "Awaiting Review";
  if (value.includes("awaitingresponse") || value === "new")
    return "Awaiting Response";
  return "Open";
};

const queryInitials = (name) =>
  String(name || "Reviewer")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const getDocumentIcon = (fileName = "") => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (extension === "pdf") return "bx-file-pdf";
  if (["xls", "xlsx", "csv"].includes(extension)) return "bx-spreadsheet";
  if (["ppt", "pptx"].includes(extension)) return "bx-slideshow";
  return "bx-file";
};

const defaultFindingConfiguration = {
  categories: [
    "Documentation",
    "Independence",
    "Quality Management",
    "Engagement Performance",
    "Professional Standards",
    "Other",
  ],
  severityLevels: [
    { name: "Critical", tone: "critical" },
    { name: "High", tone: "high" },
    { name: "Medium", tone: "medium" },
    { name: "Low", tone: "low" },
  ],
  requiredFields: ["Title", "Category", "Severity", "Description"],
};
const findingResponseRoles = [
  "Engagement Partner",
  "Engagement Manager",
  "Quality Manager",
  "Preparer",
  "Client Contact",
  "Firm Administrator",
];

const splitFindingAssignee = (value) =>
  value?.startsWith("user:")
    ? { userId: Number(value.slice(5)) || null, role: null }
    : value?.startsWith("role:")
    ? { userId: null, role: value.slice(5) || null }
    : { userId: null, role: null };

const InspectionChecklistReview = ({
  readOnly = false,
  wizardSteps,
  initialQuestionId,
  initialQueryId,
  backToMyWorkAction,
  discussionAction,
  dataId,
  inspectionId,
  dataDetail,
  workflowTask,
  selectedChecklistId,
  selectedChecklistName,
  allChecklistsAction,
  reportsAction,
  closeAction,
  breadcrumbItems = [],
}) => {
  const { registerContext } = useContextualHelp();
  const [questions, setQuestions] = useState([]);
  const [scopePreview, setScopePreview] = useState(false);
  const [hasContributorPermission, setCanRaiseFinding] = useState(false);
  const canRaiseFinding = !readOnly && hasContributorPermission;
  const [isEngagementMember, setIsEngagementMember] = useState(false);
  const [isEngagementPartner, setIsEngagementPartner] = useState(false);
  const queryQuickFilterOptions = ["All", ...(isEngagementMember ? ["Reply Required"] : []), ...(isEngagementPartner ? ["Review Required"] : []), "Open", "Awaiting Response", "Awaiting Review", "Resolved"];
  const [observationCount, setObservationCount] = useState(0);
  const [observationCreate, setObservationCreate] = useState(0);
  const [commentCreate, setCommentCreate] = useState(0);
  const [recordChoiceOpen, setRecordChoiceOpen] = useState(false);
  const [observationsRefresh, setObservationsRefresh] = useState(0);
  const conversionHandled = useRef(null);
  const [submittedInfoOpen, setSubmittedInfoOpen] = useState(false);
  const [checklistTab, setChecklistTab] = useState("checklist");
  const [checklistQueryFilter, setChecklistQueryFilter] = useState("All");
  const [checklistQuerySearch, setChecklistQuerySearch] = useState("");
  const [checklistDocuments, setChecklistDocuments] = useState([]);
  const [checklistDocumentsLoading, setChecklistDocumentsLoading] = useState(false);
  const [checklistDocumentsError, setChecklistDocumentsError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const resumeUserIdRef = useRef(null);
  const rememberQuestion = (item) => {
    if (!resumeUserIdRef.current || !item?.id || !item?.checklistId) return;
    try {
      localStorage.setItem(`piers:checklist-position:${resumeUserIdRef.current}:${inspectionId}:${item.checklistId}`, String(item.id));
    } catch { /* Review remains usable when browser storage is unavailable. */ }
  };
  const [answer, setAnswer] = useState("yes");
  const [conclusion, setConclusion] = useState("");
  const [signOffComments, setSignOffComments] = useState("");
  const [signOffCommentSaving, setSignOffCommentSaving] = useState(false);
  const [reopenConfirmOpen, setReopenConfirmOpen] = useState(false);
  const [closeReviewConfirmOpen, setCloseReviewConfirmOpen] = useState(false);
  const [bookmarkedQuestionIds, setBookmarkedQuestionIds] = useState(
    () => new Set()
  );
  const [bookmarkUpdating, setBookmarkUpdating] = useState(false);
  const [evidenceDirty, setEvidenceDirty] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const responseSavingRef = useRef(false);
  const evidenceReferencesRef = useRef(null);
  const [responseSaving, setResponseSaving] = useState(false);
  const [submissionStage, setSubmissionStage] = useState(null);
  const [search, setSearch] = useState("");
  const [navigatorFilter, setNavigatorFilter] = useState("all");
  const [navigatorFilterOpen, setNavigatorFilterOpen] = useState(false);
  const [navigatorCollapsed, setNavigatorCollapsed] = useState(true);
  const questionNavigatorRef = useRef(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [queryMode, setQueryMode] = useState(false);
  const [newQuery, setNewQuery] = useState(emptyQuery);
  const [newQueryFormOpen, setNewQueryFormOpen] = useState(false);
  const newQueryFormRef = useRef(null);

  const scrollToNewQueryForm = () => {
    newQueryFormRef.current?.scrollIntoView({
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "center",
      inline: "nearest",
    });
  };

  useEffect(() => {
    if (!newQueryFormOpen) return;
    // Wait for the form to mount and its autofocus to finish before scrolling.
    const frame = requestAnimationFrame(scrollToNewQueryForm);
    return () => cancelAnimationFrame(frame);
  }, [newQueryFormOpen]);

  const [queryAssignees, setQueryAssignees] = useState([]);
  const [queryAttachmentFiles, setQueryAttachmentFiles] = useState([]);
  const [createdQueries, setCreatedQueries] = useState([]);
  const [queriesRefreshing, setQueriesRefreshing] = useState(false);
  const [queriesRefreshError, setQueriesRefreshError] = useState("");
  const queriesPanelRef = useRef(null);
  const refreshScope = useRef(null);
  const [reviewQueries, setReviewQueries] = useState([]);
  const [querySaving, setQuerySaving] = useState(false);
  const [queryStatusFilter, setQueryStatusFilter] = useState("All");
  const [querySort, setQuerySort] = useState("oldest");
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [queryReply, setQueryReply] = useState("");
  // The closed state must never equal a query identifier returned by an API.
  const [historyOpen, setHistoryOpen] = useState(true);
  const [questionDetailTab, setQuestionDetailTab] = useState(initialQueryId ? "queries" : "evidence");
  const [activeDrawer, setActiveDrawer] = useState("queries");
  const [queryHost, setQueryHost] = useState(null);
  const [findingHost, setFindingHost] = useState(null);
  const [engagementDetailOpen, setEngagementDetailOpen] = useState(false);
  const [engagementBasics, setEngagementBasics] = useState(null);
  const [engagementBasicsLoading, setEngagementBasicsLoading] = useState(false);
  const [engagementBasicsError, setEngagementBasicsError] = useState(false);
  const [engagementCycle, setEngagementCycle] = useState(null);
  const [engagementCycleError, setEngagementCycleError] = useState(false);
  const [engagementFilesOpen, setEngagementFilesOpen] = useState(false);
  const [engagementFiles, setEngagementFiles] = useState([]);
  const [evidenceDescription, setEvidenceDescription] = useState("");
  const [evidenceUploadFiles, setEvidenceUploadFiles] = useState([]);
  const [evidenceTitle, setEvidenceTitle] = useState("");
  const [evidenceType, setEvidenceType] = useState(
    "Quality Review Documentation"
  );
  const [evidenceDragActive, setEvidenceDragActive] = useState(false);
  const [evidenceUploadOpen, setEvidenceUploadOpen] = useState(false);
  const [engagementDocumentFolders, setEngagementDocumentFolders] = useState(
    []
  );
  const [isLoadingEngagementFiles, setIsLoadingEngagementFiles] =
    useState(false);
  const [documentFolderSearch, setDocumentFolderSearch] = useState("");
  const [
    selectedEngagementDocumentFolderPath,
    setSelectedEngagementDocumentFolderPath,
  ] = useState("");
  const [findings, setFindings] = useState([]);
  const [findingConfiguration, setFindingConfiguration] = useState(
    defaultFindingConfiguration
  );
  const [findingMode, setFindingMode] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [findingWorkspace, setFindingWorkspace] = useState(null);
  const [findingActionOpen, setFindingActionOpen] = useState(null);
  const [findingSaving, setFindingSaving] = useState(false);
  const [findingErrors, setFindingErrors] = useState({});
  const [newFinding, setNewFinding] = useState({
    title: "",
    category: "",
    severity: "",
    description: "",
    recommendation: "",
    rootCause: "",
    evidenceIds: [],
    managementResponseRequired: "yes",
    responseFrom: "",
    responseDueDate: "",
    remediationRequired: "Yes",
    closureApprovalRequired: "no",
    approvalBy: "",
  });
  const [workflowResponse, setWorkflowResponse] = useState(null);
  const [workflowResponseText, setWorkflowResponseText] = useState("");
  const [workflowResponseAttachments, setWorkflowResponseAttachments] =
    useState([]);
  const [workflowResponseSaving, setWorkflowResponseSaving] = useState(false);
  const [drawerPinned, setDrawerPinned] = useState(false);
  const [reply, setReply] = useState("");
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionUsersLoading, setMentionUsersLoading] = useState(false);
  const [mentionUsersError, setMentionUsersError] = useState("");
  const [mentionUsersRetry, setMentionUsersRetry] = useState(0);
  const [replyMentions, setReplyMentions] = useState([]);
  const [mentionSearch, setMentionSearch] = useState(null);
  const discussionInputRef = useRef(null);
  const discussionHighlightRef = useRef(null);
  useEffect(() => {
    let active = true;
    setMentionUsers([]);
    setMentionUsersError("");
    setMentionUsersLoading(Boolean(inspectionId));
    if (inspectionId) getDiscussionMentionUsers(inspectionId).then(response => {
      if (!response?.status) throw new Error("Unable to load people for this review.");
      if (active) setMentionUsers(response.data || []);
    }).catch(error => {
      if (active) setMentionUsersError(error.status === 404
        ? "Mentions are unavailable. Restart the API with the latest changes."
        : "Unable to load people for this review. Try again.");
    }).finally(() => { if (active) setMentionUsersLoading(false); });
    return () => { active = false; };
  }, [inspectionId, mentionUsersRetry]);
  const [replyToId, setReplyToId] = useState(null);
  const [discussionSaving, setDiscussionSaving] = useState(false);
  const [discussionAttachmentFiles, setDiscussionAttachmentFiles] = useState(
    []
  );
  const [discussions, setDiscussions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [files, setFiles] = useState([]);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceActionOpen, setEvidenceActionOpen] = useState(null);
  const [deletingEvidenceId, setDeletingEvidenceId] = useState(null);
  const uploadRef = useRef(null);
  const discussionUploadRef = useRef(null);
  const evidenceUploadRef = useRef(null);
  const engagementId =
    dataDetail?.engagementDetail?.id || dataDetail?.engagementId;
  useEffect(() => {
    if (!engagementDetailOpen || !engagementId) return;
    let cancelled = false;
    setEngagementBasics(null);
    setEngagementBasicsLoading(true);
    setEngagementBasicsError(false);
    getEngagementDetail(engagementId).then((response) => {
      if (cancelled) return;
      if (response?.status && response.data) setEngagementBasics(response.data);
      else setEngagementBasicsError(true);
    }).catch(() => {
      if (!cancelled) setEngagementBasicsError(true);
    }).finally(() => {
      if (!cancelled) setEngagementBasicsLoading(false);
    });
    return () => { cancelled = true; };
  }, [engagementDetailOpen, engagementId]);
  const basicEngagement = engagementBasics || dataDetail?.engagementDetail;
  const engagementCycleId = dataDetail?.inspectionCycleId || basicEngagement?.inspectionCycleIds?.[0] || basicEngagement?.inspectionCycleId;
  useEffect(() => {
    setEngagementCycle(null);
    setEngagementCycleError(false);
    if (!engagementDetailOpen || !engagementCycleId) return;
    let cancelled = false;
    getInspectionCycle(engagementCycleId).then((response) => {
      if (cancelled) return;
      if (response?.status && response.data) setEngagementCycle(response.data);
      else setEngagementCycleError(true);
    }).catch(() => { if (!cancelled) setEngagementCycleError(true); });
    return () => { cancelled = true; };
  }, [engagementDetailOpen, engagementCycleId]);
  const workflowChecklistIdsKey = Array.isArray(workflowTask?.checklistIds)
    ? workflowTask.checklistIds.join(",")
    : "";

  useEffect(() => {
    registerContext("review.checklist");
    return () => registerContext(null);
  }, [registerContext]);

  useEffect(() => {
    if (!inspectionId) return;
    let cancelled = false;
    Promise.all([getInspectionChecklist(inspectionId), getLoggedInUser()])
      .then(([response, user]) => {
        if (cancelled) return;
        resumeUserIdRef.current = user?.id || user?.userId || null;
        setIsEngagementMember(Number(user?.roleId) === 4);
        setIsEngagementPartner(Number(user?.roleId) === 6 || String(user?.role?.name || user?.roleName || "").toLowerCase() === "engagement partner");
        const records = response?.data;
        if (!Array.isArray(records) || !records.length) return;

        const assignedChecklistIds = new Set(
          (workflowChecklistIdsKey ? workflowChecklistIdsKey.split(",") : [])
            .map(Number)
            .filter(Boolean)
        );
        const taskRecords = assignedChecklistIds.size
          ? records.filter((item) =>
              assignedChecklistIds.has(Number(item.checkListId))
            )
          : workflowTask?.checklistId
          ? records.filter(
              (item) =>
                Number(item.checkListId) === Number(workflowTask.checklistId)
            )
          : records;

        const availableRecords = selectedChecklistId
          ? taskRecords.filter((item) => Number(item.checkListId) === Number(selectedChecklistId))
          : taskRecords;
        const scopeView = checklistScopeView(availableRecords);
        const selectedRecords = scopeView.questions;
        setScopePreview(scopeView.preview);
        setCanRaiseFinding(canContributeToReview(user) && !scopeView.preview);
        const mappedQuestions = selectedRecords.map((item, index) => {
          const answered =
            Number(item.percentage) === 100 ||
            Number(item.inspectionStatusId) === 3;
          return {
            id: item.id ?? index,
            code: item.referenceNo || `A.${index + 1}`,
            question:
              item.question ||
              item.description ||
              `Review question ${index + 1}`,
            status: answered ? "Answered" : "Not Started",
            section:
              item.checklistArea?.name ||
              item.area?.name ||
              item.areaName ||
              item.checklistTopic?.name ||
              "Unassigned",
            response: String(item.inspectionResponse || ""),
            conclusion: item.inspectionFollowup || "",
            signOffComments: item.engagementFollowup || "",
            reference: item.workPaperRef || "",
            ...checklistQuestionGuidance(item.description),
            ...questionAnswerSettings(item.description),
            checklistId: item.checkListId,
          };
        });
        setQuestions(mappedQuestions);
        const firstPendingIndex = mappedQuestions.findIndex(
          (item) => item.status !== "Answered"
        );
        let rememberedId = null;
        const resumeChecklistId = selectedChecklistId || mappedQuestions[0]?.checklistId;
        try {
          if (resumeUserIdRef.current && resumeChecklistId)
            rememberedId = localStorage.getItem(`piers:checklist-position:${resumeUserIdRef.current}:${inspectionId}:${resumeChecklistId}`);
        } catch { /* Fall back to the first pending question. */ }
        const rememberedIndex = rememberedId ? mappedQuestions.findIndex(item => String(item.id) === rememberedId) : -1;
        const linkedIndex = initialQuestionId ? mappedQuestions.findIndex(item => Number(item.id) === Number(initialQuestionId)) : -1;
        const nextSelectedIndex = linkedIndex >= 0 ? linkedIndex : rememberedIndex >= 0 ? rememberedIndex : Math.max(0, firstPendingIndex);
        setSelectedIndex(nextSelectedIndex);
        rememberQuestion(mappedQuestions[nextSelectedIndex]);
        setAnswer(
          initialQuestionAnswer(mappedQuestions[nextSelectedIndex])
        );
        setConclusion(mappedQuestions[nextSelectedIndex]?.conclusion || "");
        setSignOffComments(
          mappedQuestions[nextSelectedIndex]?.signOffComments || ""
        );
      })
      .catch(() => {
        if (!cancelled) ErrorNotify("Unable to load this review's checklist responses.");
      });
    return () => { cancelled = true; };
  }, [inspectionId, workflowTask?.checklistId, workflowChecklistIdsKey, selectedChecklistId, initialQuestionId]);

  useEffect(() => {
    if (!inspectionId) {
      setBookmarkedQuestionIds(new Set());
      return;
    }
    getInspectionChecklistBookmarks(inspectionId)
      .then((response) =>
        setBookmarkedQuestionIds(
          new Set(
            (response?.status && Array.isArray(response.data)
              ? response.data
              : []
            ).map(String)
          )
        )
      )
      .catch(() => ErrorNotify("Unable to load bookmarked questions."));
  }, [inspectionId]);

  useEffect(() => {
    if (!engagementId) {
      setEngagementFiles([]);
      setEngagementDocumentFolders([]);
      setSelectedEngagementDocumentFolderPath("");
      return;
    }
    Promise.all([
      getEngagementDocuments(engagementId, inspectionId),
      getEngagementDocumentFolders(engagementId, inspectionId),
    ])
      .then(([documents, folders]) => {
        setEngagementFiles(
          documents?.status && Array.isArray(documents.data)
            ? documents.data
            : []
        );
        const nextFolders =
          folders?.status && Array.isArray(folders.data) ? folders.data : [];
        setEngagementDocumentFolders(nextFolders);
        setSelectedEngagementDocumentFolderPath((current) =>
          nextFolders.some((folder) => folder.path === current)
            ? current
            : nextFolders[0]?.path || ""
        );
      })
      .catch(() => setEngagementFiles([]));
  }, [engagementId, inspectionId]);

  useEffect(() => {
    if (!inspectionId) return;
    getInspectionFindingConfiguration(inspectionId)
      .then((response) => {
        if (response?.status && response.data)
          setFindingConfiguration({
            ...defaultFindingConfiguration,
            ...response.data,
            categories: response.data.categories?.length
              ? response.data.categories
              : defaultFindingConfiguration.categories,
            severityLevels: response.data.severityLevels?.length
              ? response.data.severityLevels
              : defaultFindingConfiguration.severityLevels,
            requiredFields: response.data.requiredFields?.length
              ? response.data.requiredFields
              : defaultFindingConfiguration.requiredFields,
          });
      })
      .catch(() => {
        /* Legacy/offline review keeps the same configured-form shape. */
      });
    getInspectionFindings(inspectionId)
      .then((response) => {
        if (response?.status && Array.isArray(response.data))
          setFindings(response.data);
      })
      .catch(() => {
        /* Findings can still be captured in the review workspace session. */
      });
  }, [inspectionId]);

  useEffect(() => {
    if (!inspectionId) return;
    getInspectionActivities(inspectionId)
      .then((response) =>
        setActivities(
          response?.status && Array.isArray(response.data) ? response.data : []
        )
      )
      .catch(() => setActivities([]));
  }, [inspectionId]);

  useEffect(() => {
    if (
      (!queryMode && questionDetailTab !== "queries" && !findingMode && !findingWorkspace) ||
      queryAssignees.length
    )
      return;
    Promise.all([getInvitableUsersList(), getLoggedInUser()])
      .then(([response, currentUser]) => {
        if (!response?.status) return;
        const currentUserId = String(
          currentUser?.id ?? currentUser?.userId ?? ""
        );
        const currentUserEmail = String(
          currentUser?.emailId ||
            currentUser?.email ||
            currentUser?.userName ||
            ""
        )
          .trim()
          .toLowerCase();
        setQueryAssignees(
          (response.data || [])
            .filter((user) => {
              const candidateId = String(user.id ?? user.userId ?? "");
              const candidateEmail = String(
                user.emailId || user.email || user.userName || ""
              )
                .trim()
                .toLowerCase();
              if (currentUserId && candidateId === currentUserId) return false;
              return (
                !currentUserEmail ||
                !candidateEmail ||
                candidateEmail !== currentUserEmail
              );
            })
            .map((user) => ({
              id: String(user.id),
              name:
                user.fullName ||
                user.userName ||
                user.emailId ||
                `User ${user.id}`,
            }))
        );
      })
      .catch(() => ErrorNotify("Unable to load query assignees."));
  }, [queryMode, questionDetailTab, findingMode, findingWorkspace, queryAssignees.length]);

  const selectedQuestion = questions[selectedIndex] || {};
  const saved = !evidenceDirty && answer === initialQuestionAnswer(selectedQuestion)
    && conclusion === (selectedQuestion.conclusion || "");
  const { pendingNavigation, setPendingNavigation, requestNavigation } =
    useUnsavedQuestionNavigation(!saved, responseSaving, responseSavingRef);
  const activeChecklistId = selectedQuestion.checklistId || selectedChecklistId;

  useEffect(() => {
    setNewQueryFormOpen(false);
  }, [inspectionId, selectedQuestion.id]);
  const activeChecklistQuestions = questions.filter((q) => String(q.checklistId) === String(activeChecklistId));
  const recordsForChecklist = checklistRecords(activeChecklistQuestions, reviewQueries, findings, activities);
  useEffect(() => {
    setChecklistTab("checklist");
  }, [activeChecklistId]);
  useEffect(() => {
    if (!activeChecklistId || !inspectionId) return;
    let cancelled = false;
    setChecklistDocumentsLoading(true);
    setChecklistDocumentsError("");
    setChecklistDocuments([]);
    getChecklistEvidence(inspectionId, activeChecklistId).then((response) => {
      if (cancelled) return;
      if (!response?.status) throw new Error(response?.message || "Unable to load checklist documents.");
      setChecklistDocuments(response.data || []);
    }).catch((error) => { if (!cancelled) setChecklistDocumentsError(error.message); })
      .finally(() => { if (!cancelled) setChecklistDocumentsLoading(false); });
    return () => { cancelled = true; };
  }, [checklistTab, inspectionId, activeChecklistId]);
  usePiersAssistantContext({
    screen: "Checklist",
    reviewId: Number(inspectionId),
    ...(selectedQuestion.id
      ? { checklistItemId: Number(selectedQuestion.id) }
      : {}),
  });
  useEffect(() => {
    const receive = (event) => {
      if (readOnly) return;
      const action = event.detail;
      if (
        Number(action?.reviewId) !== Number(inspectionId) ||
        Number(action?.checklistItemId) !== Number(selectedQuestion.id)
      )
        return;
      if (action.type === "DraftQuery") {
        openQueryForm();
        setNewQuery((value) => ({
          ...value,
          subject: action.draft.subject,
          details: action.draft.body,
          aiActionId: action.id,
        }));
      } else if (action.type === "DraftFinding" && canRaiseFinding) {
        openFindingForm();
        setNewFinding((value) => ({
          ...value,
          title: action.draft.subject,
          description: action.draft.body,
          recommendation: action.draft.recommendation || "",
          aiActionId: action.id,
        }));
      }
    };
    window.addEventListener("piers:assistant-draft", receive);
    return () => window.removeEventListener("piers:assistant-draft", receive);
  }, [inspectionId, selectedQuestion.id, canRaiseFinding, readOnly]);
  useEffect(() => {
    setEvidenceDescription("");
    setEvidenceUploadFiles([]);
    setEvidenceTitle("");
    setEvidenceType("Quality Review Documentation");
    setEvidenceDragActive(false);
    setEvidenceUploadOpen(false);
    if (evidenceUploadRef.current) evidenceUploadRef.current.value = "";
    if (!inspectionId || !selectedQuestion.id) {
      setFiles([]);
      return;
    }
    getInspectionEvidence(inspectionId, selectedQuestion.id, true)
      .then((response) =>
        setFiles(
          response?.status && Array.isArray(response.data) ? response.data : []
        )
      )
      .catch(() => {
        setFiles([]);
        ErrorNotify("Unable to load evidence for this question.");
      });
  }, [inspectionId, selectedQuestion.id]);
  useEffect(() => {
    let active = true;
    setQueriesRefreshError("");
    if (!inspectionId || !selectedQuestion.id) {
      setCreatedQueries([]);

      return;
    }

    getInspectionChecklistQueries(inspectionId, selectedQuestion.id)
      .then((response) => {
        if (!active) return;
        setCreatedQueries(
          response?.status && Array.isArray(response.data) ? response.data : []
        );
      })
      .catch(() => {
        if (!active) return;
        setCreatedQueries([]);
        ErrorNotify("Unable to load queries for this question.");
      });
    return () => { active = false; };
  }, [inspectionId, selectedQuestion.id]);
  useEffect(() => {
    if (!inspectionId || !selectedQuestion.id) {
      setDiscussions([]);
      setReplyToId(null);
      setDiscussionAttachmentFiles([]);
      return;
    }
    setDiscussionAttachmentFiles([]);
    if (discussionUploadRef.current) discussionUploadRef.current.value = "";
    getInspectionChecklistDiscussions(inspectionId, selectedQuestion.id)
      .then((response) =>
        setDiscussions(
          response?.status && Array.isArray(response.data) ? response.data : []
        )
      )
      .catch(() => {
        setDiscussions([]);
        ErrorNotify("Unable to load discussions for this question.");
      });
  }, [inspectionId, selectedQuestion.id]);
  useEffect(() => {
    if (!inspectionId) {
      setReviewQueries([]);
      return;
    }
    getInspectionChecklistQueries(inspectionId)
      .then((response) =>
        setReviewQueries(
          response?.status && Array.isArray(response.data) ? response.data : []
        )
      )
      .catch(() => {
        setReviewQueries([]);
        ErrorNotify("Unable to load queries for this review.");
      });
  }, [inspectionId]);
  const questionFindings = findings.filter(
    (finding) => String(finding.questionId) === String(selectedQuestion.id)
  );
  const findingAttachmentIds = new Set([
    ...questionFindings.flatMap((finding) => (finding.evidence || []).map((attachment) =>
      String(attachment.inspectionDocumentId ?? attachment.InspectionDocumentId ?? attachment.id))),
    ...(findingMode ? newFinding.evidenceIds.map(String) : []),
  ]);
  const questionEvidenceFiles = files.filter((file) =>
    Number(file.documentType) !== 5 && !findingAttachmentIds.has(String(file.id)));
  const findingEvidenceFiles = files.filter((file) =>
    newFinding.evidenceIds.some((id) => String(id) === String(file.id))
  );
  const requiredFindingFields = new Set(
    (findingConfiguration.requiredFields?.length
      ? findingConfiguration.requiredFields
      : defaultFindingConfiguration.requiredFields
    ).map((field) => String(field).replaceAll(" ", "").toLowerCase())
  );
  const querySummaryByQuestion = useMemo(
    () =>
      reviewQueries.reduce((summaries, query) => {
        const questionId = String(query.inspectionChecklistId || "");
        if (!questionId) return summaries;
        const current = summaries.get(questionId) || { open: 0, resolved: 0 };
        if (queryStage(query.status) === "Open") current.open += 1;
        else current.resolved += 1;
        summaries.set(questionId, current);
        return summaries;
      }, new Map()),
    [reviewQueries]
  );
  const filteredQuestions = useMemo(
    () =>
      questions.filter((item) => {
        const matchesSearch = `${item.code} ${item.question}`
          .toLowerCase()
          .includes(search.toLowerCase());
        const querySummary = querySummaryByQuestion.get(String(item.id));
        const matchesStatus =
          navigatorFilter === "all" ||
          (navigatorFilter === "pending" &&
            ["In Progress", "Not Started"].includes(item.status)) ||
          (navigatorFilter === "completed" && item.status === "Answered") ||
          (navigatorFilter === "awaiting" &&
            (["Flagged", "Has Query"].includes(item.status) ||
              querySummary?.open > 0));
        return matchesSearch && matchesStatus;
      }),
    [navigatorFilter, querySummaryByQuestion, questions, search]
  );
  const engagement = dataDetail?.engagementDetail?.name || "Engagement";
  const client =
    dataDetail?.engagementDetail?.client?.clientName ||
    dataDetail?.engagementDetail?.client?.name ||
    dataDetail?.engagementDetail?.clientName ||
    "—";
  const reviewType = dataDetail?.inspectionType?.name || "Engagement";
  const reviewIdentifier =
    dataDetail?.inspectionNo ||
    dataDetail?.inspectionNumber ||
    inspectionId ||
    dataId;
  const queryStageCounts = useMemo(
    () =>
      createdQueries.reduce(
        (counts, query) => ({
          ...counts,
          "Reply Required": (counts["Reply Required"] || 0) + (query.replyRequired ? 1 : 0),
          "Review Required": (counts["Review Required"] || 0) + (query.reviewRequired ? 1 : 0),
          [queryWorkspaceStatus(query.status)]:
            counts[queryWorkspaceStatus(query.status)] + 1,
        }),
        { Open: 0, "Awaiting Response": 0, "Awaiting Review": 0, Resolved: 0, Closed: 0 }
      ),
    [createdQueries]
  );
  const visibleQueries = useMemo(() => {
    return createdQueries
      .filter(
        (query) =>
          (queryStatusFilter === "Review Required" ? query.reviewRequired === true : queryStatusFilter === "Reply Required" ? query.replyRequired === true : queryStatusFilter === "All" || queryWorkspaceStatus(query.status) === queryStatusFilter)
      )
      .sort(
        (left, right) =>
          (querySort === "newest" ? -1 : 1) * (new Date(left.createdOn || 0) - new Date(right.createdOn || 0))
      );
  }, [createdQueries, queryStatusFilter, querySort]);
  const selectedQueryAttachments = (selectedQuery?.attachments || []).filter(
    (file) =>
      String(file.attachmentType || "Query").toLowerCase() !== "response"
  );
  const selectedQueryResponseAttachments = (
    selectedQuery?.attachments || []
  ).filter(
    (file) => String(file.attachmentType || "").toLowerCase() === "response"
  );
  const allQuestionCount = questions.length;
  const completedQuestionCount = questions.filter(
    (item) => item.status === "Answered"
  ).length;
  const overallProgress = questions.length
    ? Math.round((completedQuestionCount / questions.length) * 100)
    : 0;
  const reviewCompleted =
    questions.length > 0 && completedQuestionCount === questions.length;
  const isChecklistReviewerTask = canRaiseFinding && (
    !workflowTask ||
    ["ChecklistReview", "ChecklistReviewGroup"].includes(workflowTask.taskType));
  const workflowTaskActive = !readOnly && (!workflowTask || (workflowTask.taskType === "ChecklistReviewGroup"
    ? Boolean(workflowTask.workflowTaskIds?.length)
    : workflowTask.workflowStatus === "Active"));
  const isChecklistSignOffTask = !readOnly && !scopePreview && workflowTask?.taskType === "ChecklistSignOff";
  const workflowTaskLabel =
    workflowTask?.taskType === "ChecklistSignOff"
      ? "Approve Checklist Sign-off"
      : workflowTask?.taskType === "ChecklistReviewGroup"
      ? "Submit Assigned Checklists"
      : "Submit Checklist for Review";
  const workflowQuestionIds = new Set(
    questions.map((question) => String(question.id))
  );
  const openReviewQueries = reviewQueries.filter(
    (query) =>
      !/completed|closed|resolved|cancelled/i.test(query.status || "") &&
      (!workflowTask ||
        workflowQuestionIds.has(String(query.inspectionChecklistId)))
  );
  const reviewStatusLabel = openReviewQueries.length
    ? "AWAITING QUERY RESOLUTION"
    : reviewCompleted
    ? "READY TO SUBMIT"
    : "IN PROGRESS";
  const responseValue = answer;
  const isChoiceAnswer = !selectedQuestion.answerType || selectedQuestion.answerType === "Yes / No / N/A";
  const engagementDocumentFolderOptions = [...engagementDocumentFolders].sort(
    (left, right) =>
      left.path === "client-documents"
        ? -1
        : right.path === "client-documents"
        ? 1
        : (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
  );
  const selectedEngagementDocumentFolder =
    engagementDocumentFolderOptions.find(
      (folder) => folder.path === selectedEngagementDocumentFolderPath
    ) || engagementDocumentFolderOptions[0];
  const visibleEngagementDocumentFolders =
    engagementDocumentFolderOptions.filter(
      (folder) =>
        !documentFolderSearch.trim() ||
        folder.name
          .toLowerCase()
          .includes(documentFolderSearch.trim().toLowerCase())
    );
  const visibleEngagementFiles = engagementFiles.filter(
    (file) => (file.folderPath || "") === selectedEngagementDocumentFolder?.path
  );
  const navigatorSections = useMemo(() => {
    const groups = filteredQuestions.reduce((sections, question) => {
      const label = question.section || "Unassigned";
      if (!sections.has(label)) sections.set(label, []);
      sections.get(label).push(question);
      return sections;
    }, new Map());
    return Array.from(groups, ([label, sectionQuestions]) => {
      const completedCount = sectionQuestions.filter(
        (item) => item.status === "Answered"
      ).length;
      const sectionTotal = sectionQuestions.length;
      return {
        id: label,
        label,
        count: `${completedCount} / ${sectionTotal}`,
        progress: sectionTotal
          ? Math.round((completedCount / sectionTotal) * 100)
          : 0,
        questions: sectionQuestions,
      };
    });
  }, [filteredQuestions]);

  const selectQuestionAt = (index) => {
    const item = questions[index];
    if (!item) return;
    setChecklistTab("checklist");
    setSelectedIndex(index);
    setObservationCreate(0);
    rememberQuestion(item);
    setAnswer(
      initialQuestionAnswer(item)
    );
    setConclusion(item.conclusion || "");
    setSignOffComments(item.signOffComments || "");
    setSaveFailed(false);
  };

  const selectQuestion = (item) => {
    if (item.id === selectedQuestion.id) return;
    requestNavigation(() => selectQuestionAt(
      questions.findIndex((question) => question.id === item.id)
    ));
  };

  const toggleNavigatorSection = (sectionId) =>
    setCollapsedSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));

  const chooseNavigatorFilter = (filter) => {
    setNavigatorFilter(filter);
    setNavigatorFilterOpen(false);
  };

  const chooseAnswer = (value) => {
    if (readOnly) return;
    setAnswer(value);
    setSaveFailed(false);
  };

  const toggleBookmark = async () => {
    if (readOnly) return;
    if (!inspectionId || !selectedQuestion.id || bookmarkUpdating) return;
    const questionId = String(selectedQuestion.id);
    const isBookmarked = bookmarkedQuestionIds.has(questionId);
    setBookmarkUpdating(true);
    try {
      const response = await setInspectionChecklistBookmark(
        inspectionId,
        selectedQuestion.id,
        !isBookmarked
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to save bookmark.");
      setBookmarkedQuestionIds((current) => {
        const updated = new Set(current);
        if (isBookmarked) updated.delete(questionId);
        else updated.add(questionId);
        return updated;
      });
      SuccessNotify(
        isBookmarked ? "Bookmark removed." : "Question bookmarked."
      );
    } catch (error) {
      ErrorNotify(error?.message || "Unable to save bookmark.");
    } finally {
      setBookmarkUpdating(false);
    }
  };

  const validateAnswer = () => {
    if (!isChoiceAnswer && selectedQuestion.answerType !== "None" && selectedQuestion.answerRequired && !answer.trim()) {
      ErrorNotify("Enter an answer before saving.");
      return false;
    }

    return true;
  };

  const save = async () => {
    if (readOnly) return;
    if (
      !isChecklistReviewerTask ||
      !validateAnswer() ||
      !inspectionId ||
      !selectedQuestion.id ||
      responseSavingRef.current
    )
      return false;
    responseSavingRef.current = true;
    setResponseSaving(true);
    setSaveFailed(false);
    try {
      const assessment = selectedQuestion.answerType === "None" ? "" : isChoiceAnswer && answer === "na" ? "not applicable" : answer;
      const response = await saveInspectionChecklistResponse(
        inspectionId,
        selectedQuestion.id,
        { assessment, reviewerConclusion: conclusion }
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to save review response.");
      if (evidenceReferencesRef.current && !(await evidenceReferencesRef.current.save())) {
        setSaveFailed(true);
        ErrorNotify("Unable to save evidence. Your text is retained; try saving again.");
        return false;
      }
      setQuestions((items) =>
        items.map((item, index) =>
          index === selectedIndex
            ? { ...item, response: assessment, conclusion, status: "Answered" }
            : item
        )
      );
      setSaveFailed(false);
      return true;
    } catch (error) {
      setSaveFailed(true);
      ErrorNotify(error?.message || "Unable to save review response.");
      return false;
    } finally {
      responseSavingRef.current = false;
      setResponseSaving(false);
    }
  };

  const saveSignOffComments = async () => {
    if (readOnly) return;
    if (
      !isChecklistSignOffTask ||
      !inspectionId ||
      !workflowTask?.id ||
      !selectedQuestion.id ||
      signOffCommentSaving
    )
      return false;
    setSignOffCommentSaving(true);
    try {
      const response = await saveInspectionChecklistSignOffComment(
        inspectionId,
        workflowTask.id,
        selectedQuestion.id,
        signOffComments
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to save sign-off comments."
        );
      const savedComments = response.data?.signOffComments || "";
      setSignOffComments(savedComments);
      setQuestions((items) =>
        items.map((item, index) =>
          index === selectedIndex
            ? { ...item, signOffComments: savedComments }
            : item
        )
      );
      SuccessNotify(
        savedComments
          ? "Sign-off comments saved."
          : "Sign-off comments cleared."
      );
      return true;
    } catch (error) {
      ErrorNotify(error?.message || "Unable to save sign-off comments.");
      return false;
    } finally {
      setSignOffCommentSaving(false);
    }
  };

  const reopenForReview = async () => {
    if (readOnly) return;
    if (!signOffComments.trim()) {
      ErrorNotify(
        "Enter sign-off feedback before re-opening this question for review."
      );
      return;
    }
    setSignOffCommentSaving(true);
    try {
      const response = await reopenInspectionChecklistQuestionForReview(
        inspectionId,
        workflowTask.id,
        selectedQuestion.id,
        signOffComments
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to re-open this question for review."
        );
      setReopenConfirmOpen(false);
      setQuestions((items) =>
        items.map((item, index) =>
          index === selectedIndex
            ? { ...item, status: "Not Started", signOffComments }
            : item
        )
      );
      const nextIndex =
        questions.length > 1
          ? selectedIndex === questions.length - 1
            ? selectedIndex - 1
            : selectedIndex + 1
          : selectedIndex;
      selectQuestionAt(nextIndex);
      SuccessNotify(
        "Question re-opened and assigned back to the reviewer. You can continue reviewing the remaining questions."
      );
    } catch (error) {
      ErrorNotify(
        error?.message || "Unable to re-open this question for review."
      );
    } finally {
      setSignOffCommentSaving(false);
    }
  };

  const moveQuestion = async (step) => {
    if (step > 0 && isChecklistReviewerTask && !(await save())) return;
    const target = Math.max(
      0,
      Math.min(questions.length - 1, selectedIndex + step)
    );
    if (step > 0) selectQuestionAt(target);
    else requestNavigation(() => selectQuestionAt(target));
  };

  const openOutstandingQueries = () => {
    const query = openReviewQueries[0];
    const question = questions.find(
      (item) => String(item.id) === String(query?.inspectionChecklistId)
    );
    if (question) selectQuestion(question);
    setActiveDrawer("queries");
    setQuestionDetailTab("queries");
    ErrorNotify(
      `${openReviewQueries.length} open quer${
        openReviewQueries.length === 1 ? "y requires" : "ies require"
      } resolution before this review can be submitted.`
    );
  };

  const submitForApproval = async () => {
    if (readOnly) return;
    if (submissionStage) return;
    setSubmissionStage("validating");
    const needsSave = !saved || selectedQuestion.status !== "Answered";
    if (needsSave && !(await save())) {
      setSubmissionStage(null);
      return;
    }
    const hasUnansweredQuestions = questions.some(
      (question, index) =>
        index !== selectedIndex && question.status !== "Answered"
    );
    if (hasUnansweredQuestions) {
      setSubmissionStage(null);
      ErrorNotify(
        "Complete all checklist questions before finishing the review."
      );
      return;
    }
    if (openReviewQueries.length) {
      setSubmissionStage(null);
      openOutstandingQueries();
      return;
    }
    try {
      setSubmissionStage("generating");
      const response = await submitInspectionForApproval(inspectionId);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to submit this review for approval."
        );
      setSubmissionStage("submitted");
      SuccessNotify(
        "Review submitted for approval."
      );
      (allChecklistsAction || closeAction)?.({
        openSummary: true,
        status: response.data?.status || "Awaiting Approval",
      });
    } catch (error) {
      setSubmissionStage(null);
      ErrorNotify(
        error?.message || "Unable to submit this review for approval."
      );
    }
  };

  const completeWorkflowTask = async () => {
    if (readOnly) return;
    if (!workflowTask || !workflowTaskActive || submissionStage) return;
    setSubmissionStage("validating");
    if (isChecklistReviewerTask) {
      const needsSave = !saved || selectedQuestion.status !== "Answered";
      if (needsSave && !(await save())) {
        setSubmissionStage(null);
        return;
      }
    }
    if (questions.some((question, index) =>
      (index !== selectedIndex || !isChecklistReviewerTask) && question.status !== "Answered"
    )) {
      setSubmissionStage(null);
      ErrorNotify(
        "Complete every question in this checklist before submitting it for review."
      );
      return;
    }
    if (openReviewQueries.length) {
      setSubmissionStage(null);
      openOutstandingQueries();
      return;
    }
    try {
      setSubmissionStage("generating");
      const workflowTaskIds =
        Array.isArray(workflowTask.workflowTaskIds) &&
        workflowTask.workflowTaskIds.length
          ? workflowTask.workflowTaskIds
          : [workflowTask.id].filter(Boolean);
      if (!workflowTaskIds.length)
        throw new Error(
          "There are no active checklist assignments to complete."
        );
      let response = null;

      for (const workflowTaskId of workflowTaskIds) {
        response = await completeInspectionReviewWorkflowTask(
          inspectionId,
          workflowTaskId
        );
        if (!response?.status)
          throw new Error(
            response?.message || "Unable to complete this workflow task."
          );
      }
      setSubmissionStage("submitted");
      SuccessNotify(
        (response.data?.status === "Awaiting Approval"
          ? "Assigned checklists approved. Final sign-off is now pending."
          : "Assigned checklists submitted for the next review step.")
      );
      (allChecklistsAction || closeAction)?.({
        openSummary: true,
        status: response.data?.status || "In Progress",
      });
    } catch (error) {
      setSubmissionStage(null);
      ErrorNotify(error?.message || "Unable to complete this workflow task.");
    }
  };
  const discussionMessages = useMemo(() => {
    const byId = new Map(
      discussions.map((discussion) => [
        String(discussion.id),
        { ...discussion, replies: [] },
      ])
    );
    const roots = [];
    byId.forEach((discussion) => {
      const parent = discussion.parentDiscussionId
        ? byId.get(String(discussion.parentDiscussionId))
        : null;
      if (parent) parent.replies.push(discussion);
      else roots.push(discussion);
    });
    const flattened = [];
    const newestFirst = (left, right) =>
      (Date.parse(right.createdOn) || 0) - (Date.parse(left.createdOn) || 0) ||
      Number(right.id) - Number(left.id);

    const appendMessages = (message, depth) => {
      flattened.push({ ...message, depth });
      message.replies.sort(newestFirst).forEach((child) => appendMessages(child, depth + 1));
    };
    roots.sort(newestFirst).forEach((message) => appendMessages(message, 0));
    return flattened;
  }, [discussions]);

  const findMention = (value, caret, mentions = replyMentions) => {
    setMentionSearch(activeMention(value, caret, mentions));
  };
  const mentionOptions = mentionSearch ? mentionUsers.filter(user =>
    String(user.name || "").toLowerCase().includes(mentionSearch.term.toLowerCase())).slice(0, 8) : [];
  const selectMention = (user) => {
    if (!mentionSearch) return;
    const label = `@${user.name}`;
    const next = reply.slice(0, mentionSearch.start) + label + " " + reply.slice(mentionSearch.end);
    if (next.length > 4000) return;
    setReplyMentions([...updateMentionRanges(reply, next, replyMentions), { userId: user.id, start: mentionSearch.start, length: label.length }]);
    setReply(next);
    setMentionSearch(null);
    requestAnimationFrame(() => {
      discussionInputRef.current?.focus();
      discussionInputRef.current?.setSelectionRange(mentionSearch.start + label.length + 1, mentionSearch.start + label.length + 1);
    });
  };
  const renderDiscussionMessage = (discussion) => {
    const parts = [];
    let end = 0;
    for (const mention of readDiscussionMentions(discussion.message, discussion.mentionsJson)) {
      parts.push(discussion.message.slice(end, mention.start));
      parts.push(<strong className="checklist-review__mention" key={mention.start}>{discussion.message.slice(mention.start, mention.start + mention.length)}</strong>);
      end = mention.start + mention.length;
    }
    parts.push(discussion.message.slice(end));
    return parts;
  };
  const addReply = async () => {
    if (readOnly) return;
    const message = reply.trim();
    if (!message || !inspectionId || !selectedQuestion.id || discussionSaving)
      return;
    setDiscussionSaving(true);
    try {
      const response = await createInspectionChecklistDiscussion(inspectionId, {
        inspectionChecklistId: Number(selectedQuestion.id),
        parentDiscussionId: replyToId || null,
        message,
        mentions: replyMentions.map(item => ({ ...item, start: item.start - (reply.length - reply.trimStart().length) })),
      });
      if (!response?.status)
        throw new Error(response?.message || "Unable to post discussion.");
      const uploadedAttachments = [];
      const failedAttachments = [];
      for (const file of discussionAttachmentFiles) {
        try {
          const formData = new FormData();
          formData.append("document", file);
          const uploadResponse =
            await uploadInspectionChecklistDiscussionAttachment(
              inspectionId,
              response.data.id,
              formData
            );
          if (!uploadResponse?.status)
            throw new Error(
              uploadResponse?.message || `Unable to upload ${file.name}.`
            );
          uploadedAttachments.push(uploadResponse.data);
        } catch {
          failedAttachments.push(file.name);
        }
      }
      setDiscussions((items) => [
        ...items,
        { ...response.data, attachments: uploadedAttachments },
      ]);
      setReply("");
      setReplyMentions([]);
      setMentionSearch(null);
      setReplyToId(null);
      setDiscussionAttachmentFiles([]);
      if (discussionUploadRef.current) discussionUploadRef.current.value = "";
      if (response.data?.notificationFailed)
        ErrorNotify("Discussion posted, but a mention notification could not be delivered.");
      else if (failedAttachments.length)
        ErrorNotify(
          `Discussion posted, but ${failedAttachments.length} attachment${
            failedAttachments.length === 1 ? "" : "s"
          } could not be uploaded.`
        );
      else
        SuccessNotify(
          discussionAttachmentFiles.length
            ? "Discussion and attachments posted."
            : "Discussion posted."
        );
    } catch (error) {
      ErrorNotify(error?.message || "Unable to post discussion.");
    } finally {
      setDiscussionSaving(false);
    }
  };

  const stageDiscussionAttachments = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length) {
      setDiscussionAttachmentFiles((current) => [
        ...current,
        ...selectedFiles.filter(
          (file) =>
            !current.some(
              (item) => item.name === file.name && item.size === file.size
            )
        ),
      ]);
    }
    event.target.value = "";
  };

  const addFile = async (event) => {
    if (readOnly) return;
    const file = event.target.files?.[0];
    if (!file || !inspectionId || !selectedQuestion.id || evidenceUploading)
      return;
    setEvidenceUploading(true);
    try {
      const formData = new FormData();
      formData.append("inspectionChecklistId", String(selectedQuestion.id));
      formData.append("document", file);
      formData.append("findingAttachment", "true");
      const response = await uploadInspectionEvidence(inspectionId, formData);
      if (!response?.status)
        throw new Error(response?.message || "Unable to upload evidence.");
      setFiles((items) => [response.data, ...items]);
      if (findingMode) {
        setNewFinding((current) => ({
          ...current,
          evidenceIds: current.evidenceIds.some(
            (id) => String(id) === String(response.data.id)
          )
            ? current.evidenceIds
            : [...current.evidenceIds, response.data.id],
        }));
      }
      SuccessNotify("Evidence uploaded and recorded.");
    } catch (error) {
      ErrorNotify(error?.message || "Unable to upload evidence.");
    } finally {
      setEvidenceUploading(false);
    }
    event.target.value = "";
  };

  const uploadEvidenceWithDescription = async (event) => {
    if (readOnly) return;
    event.preventDefault();
    const description = evidenceDescription.trim();
    if (
      !evidenceUploadFiles.length ||
      !evidenceTitle.trim() ||
      !evidenceType ||
      !description ||
      !inspectionId ||
      !selectedQuestion.id ||
      evidenceUploading
    )
      return;
    setEvidenceUploading(true);
    const uploaded = [];
    const failed = [];
    try {
      for (const file of evidenceUploadFiles) {
        try {
          const formData = new FormData();
          formData.append("inspectionChecklistId", String(selectedQuestion.id));
          formData.append("title", evidenceTitle.trim());
          formData.append("evidenceType", evidenceType);
          formData.append("description", description);
          formData.append("document", file);
          const response = await uploadInspectionEvidence(
            inspectionId,
            formData
          );
          if (!response?.status)
            throw new Error(
              response?.message || `Unable to upload ${file.name}.`
            );
          uploaded.push(response.data);
        } catch {
          failed.push(file);
        }
      }
      if (uploaded.length)
        setFiles((items) => [...uploaded.reverse(), ...items]);
      if (failed.length) {
        setEvidenceUploadFiles(failed);
        ErrorNotify(
          `${failed.length} file${
            failed.length === 1 ? "" : "s"
          } could not be uploaded. Please try again.`
        );
      } else {
        setEvidenceDescription("");
        setEvidenceUploadFiles([]);
        setEvidenceTitle("");
        setEvidenceType("Quality Review Documentation");
        setEvidenceUploadOpen(false);
        if (evidenceUploadRef.current) evidenceUploadRef.current.value = "";
        SuccessNotify(
          `${uploaded.length} evidence file${
            uploaded.length === 1 ? "" : "s"
          } uploaded.`
        );
      }
    } catch (error) {
      ErrorNotify(error?.message || "Unable to upload evidence.");
    } finally {
      setEvidenceUploading(false);
    }
  };

  const addEvidenceUploadFiles = (selectedFiles) => {
    if (readOnly) return;
    const filesToAdd = Array.from(selectedFiles || []);
    const validFiles = filesToAdd.filter(
      (file) =>
        queryAttachmentExtensions.some((extension) =>
          file.name.toLowerCase().endsWith(extension)
        ) && file.size <= 50 * 1024 * 1024
    );
    if (validFiles.length !== filesToAdd.length)
      ErrorNotify(
        "Evidence files must be PDF, Office documents, PNG, or JPG files no larger than 50 MB each."
      );
    if (!validFiles.length) return;
    setEvidenceUploadFiles((current) => [
      ...current,
      ...validFiles.filter(
        (file) =>
          !current.some(
            (item) => item.name === file.name && item.size === file.size
          )
      ),
    ]);
    if (!evidenceTitle.trim() && validFiles.length === 1)
      setEvidenceTitle(validFiles[0].name.replace(/\.[^.]+$/, ""));
  };

  const stageEvidenceUploadFiles = (event) => {
    if (readOnly) return;
    addEvidenceUploadFiles(event.target.files);
    event.target.value = "";
  };

  const discardEvidenceUpload = () => {
    if (evidenceUploading) return;
    setEvidenceUploadOpen(false);
    setEvidenceDescription("");
    setEvidenceUploadFiles([]);
    setEvidenceTitle("");
    setEvidenceType("Quality Review Documentation");
    setEvidenceDragActive(false);
    if (evidenceUploadRef.current) evidenceUploadRef.current.value = "";
  };

  const addQueryAttachments = (selectedFiles) => {
    if (readOnly) return;
    const validFiles = selectedFiles.filter(
      (file) =>
        queryAttachmentExtensions.some((extension) =>
          file.name.toLowerCase().endsWith(extension)
        ) && file.size <= 25 * 1024 * 1024
    );
    if (validFiles.length !== selectedFiles.length)
      ErrorNotify(
        "Attachments must be PDF, Office documents, PNG, or JPG files no larger than 25 MB each."
      );
    if (validFiles.length) {
      setQueryAttachmentFiles((current) => [
        ...current,
        ...validFiles.filter(
          (file) =>
            !current.some(
              (item) => item.name === file.name && item.size === file.size
            )
        ),
      ]);
    }
  };

  const stageQueryAttachments = (event) => {
    if (readOnly) return;
    const selectedFiles = Array.from(event.target.files || []);
    addQueryAttachments(selectedFiles);
    event.target.value = "";
  };

  const saveQuery = async () => {
    if (readOnly) return;
    const details = newQuery.details.trim();
    const subject = newQuery.subject.trim() || details.replace(/\s+/g, " ").slice(0, 200);
    const assignee = queryAssignees.find(
      (user) => user.id === newQuery.assigneeId
    );
    if (
      !details ||
      !assignee ||
      !inspectionId ||
      !selectedQuestion.id
    )
      return;
    setQuerySaving(true);
    try {
      const payload = {
        aiActionId: newQuery.aiActionId,
        inspectionChecklistId: Number(selectedQuestion.id),
        assignedTo: Number(assignee.id),
        subject,
        details,
        priority: newQuery.priority,
        dueDate: newQuery.dueDate || null,
        queryType: newQuery.queryType,
        notifyAssigneeByEmail: newQuery.notifyAssigneeByEmail,
      };
      const isEditing = queryMode === "edit" && selectedQuery?.queryId;
      const response = isEditing
        ? await updateInspectionChecklistQuery(
            inspectionId,
            selectedQuery.queryId,
            payload
          )
        : await createInspectionChecklistQuery(inspectionId, payload);
      if (!response?.status)
        throw new Error(
          response?.message ||
            `Unable to ${isEditing ? "update" : "create"} the query task.`
        );
      const uploadedAttachments = [];
      let attachmentFailures = 0;
      for (const file of queryAttachmentFiles) {
        try {
          const formData = new FormData();
          formData.append("attachmentType", "Query");
          formData.append("document", file);
          const uploadResponse = await uploadInspectionChecklistQueryAttachment(
            inspectionId,
            response.data.queryId,
            formData
          );
          if (!uploadResponse?.status)
            throw new Error(
              uploadResponse?.message || "Unable to upload query attachment."
            );
          uploadedAttachments.push(uploadResponse.data);
        } catch {
          attachmentFailures += 1;
        }
      }
      const savedQuery = {
        ...(isEditing
          ? selectedQuery
          : {
              canClose: true,
              canReply: true,
              nextMessageType: "FollowUp",
              messages: [],
            }),
        ...response.data,
        attachments: [
          ...(isEditing
            ? selectedQuery?.attachments || []
            : response.data.attachments || []),
          ...uploadedAttachments,
        ],
      };
      if (isEditing) {
        setCreatedQueries((items) =>
          items.map((item) =>
            item.queryId === selectedQuery.queryId ? savedQuery : item
          )
        );
        setReviewQueries((items) =>
          items.map((item) =>
            item.queryId === selectedQuery.queryId ? savedQuery : item
          )
        );
      } else {
        setCreatedQueries((items) => [savedQuery, ...items]);
        setReviewQueries((items) => [savedQuery, ...items]);
      }
      setNewQuery(emptyQuery());
      setNewQueryFormOpen(false);
      setQueryAttachmentFiles([]);
      setSelectedQuery(null);
      setQueryMode(null);
      if (attachmentFailures)
        ErrorNotify(
          `${attachmentFailures} query attachment${
            attachmentFailures === 1 ? "" : "s"
          } could not be uploaded. The query was still saved.`
        );
      else
        SuccessNotify(
          isEditing
            ? "Query updated."
            : "Query created as a task and assigned to the selected user."
        );
    } catch (error) {
      ErrorNotify(
        error?.message ||
          `Unable to ${
            queryMode === "edit" ? "update" : "create"
          } the query task.`
      );
    } finally {
      setQuerySaving(false);
    }
  };

  const openQueryForm = (initialDetails = "") => {
    if (readOnly) return;
    if (!canRaiseFinding) return;
    setQueryMode("create");
    setActiveDrawer("queries");
    setQuestionDetailTab("queries");
    setSelectedQuery(null);

    setNewQuery({ ...emptyQuery(), details: typeof initialDetails === "string" ? initialDetails : "" });
    setQueryAttachmentFiles([]);
  };

  const openQuery = (query, mode) => {
    if (mode === "view") {
      openWorkflowResponse("query", query);
      return;
    }
    setSelectedQuery(query);
    setQueryMode(readOnly ? null : mode);
    setActiveDrawer("queries");
    setQuestionDetailTab("queries");

    setQueryAttachmentFiles([]);
    setQueryReply("");
    setNewQuery({
      subject: query.subject || "",
      details: query.details || "",
      priority: query.priority || "Medium",
      assigneeId: query.assigneeId ? String(query.assigneeId) : "",
      dueDate: query.dueDate ? String(query.dueDate).slice(0, 10) : "",
      queryType: query.queryType || "Clarification",
      notifyAssigneeByEmail: true,
    });
  };

  const renderQueryActions = (query) => (
    <QueryActionMenu readOnly={readOnly} query={query} onOpen={openQuery} />
  );

  const refreshQueryConversation = async (queryId) => {
    const scope = refreshScope.current;
    const [result, review] = await Promise.all([
      getInspectionChecklistQueries(inspectionId, undefined, queryId),
      getQueryResponseReview(inspectionId, queryId),
    ]);
    if (!result?.status || !Array.isArray(result.data))
      throw new Error(
        result?.message || "Unable to refresh the query conversation."
      );
    const refreshed = result.data.find(
      (item) => Number(item.queryId || item.id) === Number(queryId)
    );
    if (!refreshed) throw new Error("The query is no longer available.");
    if (!review?.status) throw new Error(review?.message || "Unable to refresh response review.");
    if (scope !== refreshScope.current) return refreshed;
    refreshed.refreshSnapshot = review.data;
    preserveQueryPosition(queriesPanelRef.current);
    setReviewQueries((items) => items.map((item) => Number(item.queryId || item.id) === Number(queryId) ? refreshed : item));
    setCreatedQueries((items) =>
      items.map((item) =>
        Number(item.queryId || item.id) === Number(queryId) ? refreshed : item
      )
    );
    setSelectedQuery((current) => Number(current?.queryId || current?.id) === Number(queryId) ? refreshed : current);
    return refreshed;
  };

  refreshScope.current = `${inspectionId}:${selectedQuestion.id}`;
  const refreshQueriesSection = async () => {
    if (queriesRefreshing) return;
    const scope = refreshScope.current;
    setQueriesRefreshing(true); setQueriesRefreshError("");
    try {
      const result = await getInspectionChecklistQueries(inspectionId, selectedQuestion.id);
      if (!result?.status || !Array.isArray(result.data)) throw new Error(result?.message || "Unable to refresh queries.");
      const refreshed = await Promise.all(result.data.map(async (item) => {
        const review = await getQueryResponseReview(inspectionId, item.queryId || item.id);
        if (!review?.status) throw new Error(review?.message || "Unable to refresh response review.");
        return { ...item, refreshSnapshot: review.data };
      }));
      if (scope !== refreshScope.current) return;
      preserveQueryPosition(queriesPanelRef.current);
      setCreatedQueries((current) => [...refreshed, ...current.filter((old) => !refreshed.some((item) => Number(item.queryId || item.id) === Number(old.queryId || old.id))).map((old) => ({ ...old, refreshUnavailable: true }))]);
      setReviewQueries((current) => current.map((old) => refreshed.find((item) => Number(item.queryId || item.id) === Number(old.queryId || old.id)) || old));
    } catch (failure) {
      if (scope === refreshScope.current) setQueriesRefreshError(failure.message || "Unable to refresh queries.");
    } finally { setQueriesRefreshing(false); }
  };

  const submitQueryReply = async () => {
    if (readOnly) return;
    const text = queryReply.trim();
    if (!inspectionId || !selectedQuery?.queryId || !text || querySaving)
      return;
    setQuerySaving(true);
    try {
      const response = await respondToInspectionChecklistQuery(
        inspectionId,
        selectedQuery.queryId,
        { response: text }
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to add the query message."
        );
      await refreshQueryConversation(selectedQuery.queryId);
      setQueryReply("");
      SuccessNotify(
        response.data?.type === "FollowUp"
          ? "Follow-up query sent."
          : "Query response posted."
      );
    } catch (error) {
      ErrorNotify(error?.message || "Unable to add the query message.");
    } finally {
      setQuerySaving(false);
    }
  };

  const closeQueryConversation = async () => {
    if (readOnly) return;
    if (
      !inspectionId ||
      !selectedQuery?.queryId ||
      querySaving ||
      !window.confirm(
        "Close this query? The complete conversation will remain available in the audit history."
      )
    )
      return;
    setQuerySaving(true);
    try {
      const response = await closeInspectionChecklistQuery(
        inspectionId,
        selectedQuery.queryId
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to close the query.");
      await refreshQueryConversation(selectedQuery.queryId);
      SuccessNotify("Query closed.");
    } catch (error) {
      ErrorNotify(error?.message || "Unable to close the query.");
    } finally {
      setQuerySaving(false);
    }
  };

  const [queryToDelete, setQueryToDelete] = useState(null);
  const [queryDeleteError, setQueryDeleteError] = useState("");
  const requestDeleteQuery = (query) => {
    if (readOnly || querySaving || !inspectionId || !query?.queryId) return;
    setQueryDeleteError("");
    setQueryToDelete(query);
  };
  const deleteQuery = async () => {
    const query = queryToDelete;
    if (readOnly) return;
    if (
      !inspectionId ||
      !query?.queryId ||
      querySaving
    )
      return;
    setQuerySaving(true);
    setQueryDeleteError("");

    try {
      const response = await deleteInspectionChecklistQuery(
        inspectionId,
        query.queryId
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to delete the query.");
      setCreatedQueries((items) =>
        items.filter((item) => item.queryId !== query.queryId)
      );
      setReviewQueries((items) =>
        items.filter((item) => item.queryId !== query.queryId)
      );
      if (selectedQuery?.queryId === query.queryId) {
        setSelectedQuery(null);
        setQueryMode(null);
      }
      setQueryToDelete(null);
      SuccessNotify("Query deleted.");
    } catch (error) {
      setQueryDeleteError(error?.message || "Unable to delete the query.");
    } finally {
      setQuerySaving(false);
    }
  };

  const closeActivityForms = () => {
    evidenceReferencesRef.current?.close();
    setNewQueryFormOpen(false);
    setCommentCreate(0);
    setObservationCreate(0);
  };

  const openFindingForm = () => {
    if (readOnly) return;
    if (!canRaiseFinding) return;
    closeActivityForms();
    setFindingMode("create");
    setSelectedFinding(null);
    setFindingActionOpen(null);
    setFindingErrors({});
    setActiveDrawer("findings");
    setQuestionDetailTab("findings");
    setNewFinding({
      deficiencyExists: false,
      title: "",
      category: "",
      severity: "",
      description: "",
      recommendation: "",
      rootCause: "",
      evidenceIds: [],
      managementResponseRequired: "yes",
      responseFrom: "",
      responseDueDate: "",
      remediationRequired: "Yes",
      closureApprovalRequired: "no",
      approvalBy: "",
    });
  };

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("observationId");
    if (!id || !questions.length || !canRaiseFinding || conversionHandled.current === id) return;
    conversionHandled.current = id;
    getData(`/inspection/${inspectionId}/observations`).then((response) => {
      if (!response?.status) throw new Error(response?.message || "Unable to load observation.");
      const observation = (response.data || []).find((item) => String(item.id) === id);
      const index = questions.findIndex((question) => Number(question.id) === Number(observation?.questionId));
      if (!observation || observation.convertedFindingId || index < 0) throw new Error("Observation cannot be converted from this checklist.");
      setSelectedIndex(index);
    setObservationCreate(0); openFindingForm();
      setNewFinding((current) => ({ ...current, description: observation.description, observationId: observation.id }));
    }).catch((failure) => ErrorNotify(failure.message));
  }, [questions.length, canRaiseFinding, inspectionId]);
  const openFindingForEdit = async (finding) => {
    if (readOnly) return;
    if (!Array.isArray(finding?.evidence) && inspectionId) {
      setFindingSaving(true);
      try {
        const response = await getInspectionFinding(inspectionId, finding.id);
        if (!response?.status)
          throw new Error(
            response?.message || "Unable to load the finding details."
          );
        await openFindingForEdit(response.data);
      } catch (error) {
        ErrorNotify(error?.message || "Unable to load the finding details.");
      } finally {
        setFindingSaving(false);
      }
      return;
    }
    const evidenceIds = (finding.evidence || [])
      .map(
        (evidence) =>
          evidence.inspectionDocumentId ?? evidence.InspectionDocumentId
      )
      .filter(Boolean);
    setSelectedFinding(finding);
    setFindingMode("edit");
    setFindingActionOpen(null);
    setFindingErrors({});
    setActiveDrawer("findings");
    setQuestionDetailTab("findings");
    setNewFinding({
      ...finding,
      remedialActionsDate: finding.remedialActionsDate?.slice(0, 10) || "",
      findingResolvedDate: finding.findingResolvedDate?.slice(0, 10) || "",
      title: finding.title || "",
      category: finding.category || "",
      severity: finding.severity || "",
      description: finding.description || "",
      recommendation: finding.recommendation || "",
      rootCause: finding.rootCause || "",
      evidenceIds,
      managementResponseRequired: finding.managementResponseRequired
        ? "yes"
        : "no",
      responseFrom: finding.responseRequestedFromUserId
        ? `user:${finding.responseRequestedFromUserId}`
        : finding.responseRequestedFromRole
        ? `role:${finding.responseRequestedFromRole}`
        : "",
      responseDueDate: finding.responseDueDate
        ? String(finding.responseDueDate).slice(0, 10)
        : "",
      remediationRequired:
        finding.remediationRequired || "DetermineAfterResponse",
      closureApprovalRequired: finding.closureApprovalRequired ? "yes" : "no",
      approvalBy: finding.closureApproverUserId
        ? `user:${finding.closureApproverUserId}`
        : finding.closureApproverRole
        ? `role:${finding.closureApproverRole}`
        : "",
    });
  };

  const openFindingWorkspace = async (finding) => {
    setFindingActionOpen(null);
    setFindingWorkspace({
      ...finding,
      questionCode: `${selectedQuestion.code} - ${selectedQuestion.question}`,
      detailsLoading: true,
    });
    setFindingSaving(true);
    try {
      const response = await getInspectionFinding(inspectionId, finding.id);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to load the finding details."
        );
      setFindingWorkspace({
        ...response.data,
        questionCode: `${selectedQuestion.code} - ${selectedQuestion.question}`,
      });
    } catch (error) {
      setFindingWorkspace(null);
      ErrorNotify(error?.message || "Unable to load the finding details.");
    } finally {
      setFindingSaving(false);
    }
  };

  const updateFindingWorkspace = (findingId, update) => {
    setFindings((items) =>
      items.map((item) =>
        item.id === findingId ? { ...item, ...update } : item
      )
    );
    setFindingWorkspace((item) =>
      item?.id === findingId ? { ...item, ...update } : item
    );
  };

  const postFindingComment = async (message) => {
    if (readOnly) return;
    if (!findingWorkspace || findingSaving) return;
    setFindingSaving(true);
    try {
      const response = await addInspectionFindingMessage(
        inspectionId,
        findingWorkspace.id,
        { message }
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to post the comment.");
      const nextMessages = [
        ...(findingWorkspace.messages || []),
        response.data,
      ];
      updateFindingWorkspace(findingWorkspace.id, {
        messages: nextMessages,
        updatedOn: response.data?.createdOn,
      });
      SuccessNotify("Comment posted.");
      return true;
    } catch (error) {
      ErrorNotify(error?.message || "Unable to post the comment.");
      return false;
    } finally {
      setFindingSaving(false);
    }
  };

  const transitionFinding = async (action, comment) => {
    if (readOnly) return;
    if (!findingWorkspace || findingSaving) return;
    setFindingSaving(true);
    try {
      const response = await transitionInspectionFinding(
        inspectionId,
        findingWorkspace.id,
        { action, comment }
      );
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to update the finding workflow."
        );
      const [refreshed, detail] = await Promise.all([
        getInspectionFindings(inspectionId, selectedQuestion.id),
        getInspectionFinding(inspectionId, findingWorkspace.id),
      ]);
      if (refreshed?.status && Array.isArray(refreshed.data)) {
        setFindings((items) => [
          ...items.filter(
            (item) => String(item.questionId) !== String(selectedQuestion.id)
          ),
          ...refreshed.data,
        ]);
        if (detail?.status)
          setFindingWorkspace({
            ...detail.data,
            questionCode: `${selectedQuestion.code} - ${selectedQuestion.question}`,
          });
        else updateFindingWorkspace(findingWorkspace.id, response.data || {});
      } else updateFindingWorkspace(findingWorkspace.id, response.data || {});
      SuccessNotify(
        `Finding moved to ${response.data?.findingStatus || "the next stage"}.`
      );
      return true;
    } catch (error) {
      ErrorNotify(error?.message || "Unable to update the finding workflow.");
      return false;
    } finally {
      setFindingSaving(false);
    }
  };

  const saveFinding = async () => {
    if (readOnly) return;
    const finding = normalizeFinding(newFinding);
    const errors = validateFindingDetails(finding, requiredFindingFields);
    if (finding.deficiencyExists === true) {
      if (requiredFindingFields.has("recommendation") && !finding.recommendation?.trim()) errors.recommendation = "Recommendation is required.";
      if (finding.managementResponseRequired === "yes") {
        if (!finding.responseFrom) errors.responseFrom = "Select who needs to respond.";
        if (!finding.responseDueDate) errors.responseDueDate = "A response due date is required.";
      }
      if (finding.closureApprovalRequired === "yes" && !finding.approvalBy) errors.approvalBy = "Select who must approve closure.";
    }
    if (Object.keys(errors).length) {
      setFindingErrors(errors);
      return;
    }
    const responseAssignee = splitFindingAssignee(finding.responseFrom);
    const approvalAssignee = splitFindingAssignee(finding.approvalBy);
    const {
      responseFrom,
      approvalBy,
      managementResponseRequired,
      closureApprovalRequired,
      ...findingFields
    } = finding;
    const isEditing = findingMode === "edit" && selectedFinding?.id;
    const payload = {
      ...findingFields,
      remedialActionsDate: findingFields.remedialActionsDate || null,
      findingResolvedDate: findingFields.findingResolvedDate || (findingFields.findingResolved ? findingFields.remedialActionsDate : null) || null,
      sourceType: "ChecklistQuestion",
      questionId: Number(selectedQuestion.id) || null,
      ...(isEditing ? {} : { findingStatus: "Open" }),
      managementResponseRequired: managementResponseRequired === "yes",
      responseRequestedFromUserId:
        managementResponseRequired === "yes" ? responseAssignee.userId : null,
      responseRequestedFromRole:
        managementResponseRequired === "yes" ? responseAssignee.role : null,
      responseDueDate:
        managementResponseRequired === "yes"
          ? finding.responseDueDate
          : null,
      closureApprovalRequired: closureApprovalRequired === "yes",
      closureApproverUserId:
        closureApprovalRequired === "yes" ? approvalAssignee.userId : null,
      closureApproverRole:
        closureApprovalRequired === "yes" ? approvalAssignee.role : null,
    };
    setFindingSaving(true);
    try {
      const response = inspectionId
        ? isEditing
          ? await updateInspectionFinding(
              inspectionId,
              selectedFinding.id,
              payload
            )
          : await createInspectionFinding(inspectionId, payload)
        : null;
      if (inspectionId && !response?.status)
        throw new Error(
          response?.message ||
            `Unable to ${isEditing ? "update" : "save"} the finding.`
        );
      if (!isEditing || payload.observationId) {
        setObservationCreate(0);
        setObservationsRefresh((value) => value + 1);
      }
      const savedFinding =
        response?.data ||
        (isEditing
          ? { ...selectedFinding, ...payload }
          : {
              ...payload,
              id: `local-${Date.now()}`,
              findingNumber: `FND-${String(findings.length + 1).padStart(
                3,
                "0"
              )}`,
              createdOn: new Date().toISOString(),
              findingStatus: "Open",
            });
      const refreshed = inspectionId
        ? await getInspectionFindings(inspectionId, selectedQuestion.id)
        : null;
      if (refreshed?.status && Array.isArray(refreshed.data))
        setFindings((items) => [
          ...items.filter(
            (item) => String(item.questionId) !== String(selectedQuestion.id)
          ),
          ...refreshed.data,
        ]);
      else
        setFindings((items) =>
          isEditing
            ? items.map((item) =>
                item.id === selectedFinding.id ? savedFinding : item
              )
            : [savedFinding, ...items]
        );
      setFindingMode(false);
      setSelectedFinding(null);
      setFindingErrors({});
      SuccessNotify(
        isEditing
          ? "Finding updated."
          : `Finding ${savedFinding.findingNumber} created successfully.`
      );
    } catch (error) {
      ErrorNotify(
        error?.message || `Unable to ${isEditing ? "update" : "save"} finding.`
      );
    } finally {
      setFindingSaving(false);
    }
  };

  const deleteFinding = async (finding) => {
    if (readOnly) return;
    if (
      !inspectionId ||
      !finding?.id ||
      findingSaving ||
      !window.confirm(
        `Delete ${
          finding.findingNumber || "this finding"
        }? This cannot be undone.`
      )
    )
      return;
    setFindingSaving(true);
    setFindingActionOpen(null);
    try {
      const response = await deleteInspectionFinding(inspectionId, finding.id);
      if (!response?.status)
        throw new Error(response?.message || "Unable to delete the finding.");
      setFindings((items) => items.filter((item) => item.id !== finding.id));
      if (selectedFinding?.id === finding.id) {
        setSelectedFinding(null);
        setFindingMode(false);
      }
      SuccessNotify("Finding deleted.");
    } catch (error) {
      ErrorNotify(error?.message || "Unable to delete the finding.");
    } finally {
      setFindingSaving(false);
    }
  };

  const openWorkflowResponse = (type, item) => {
    if (readOnly) return;

    setFindingActionOpen(null);
    const evidenceIds = new Set(
      (item.evidence || []).map((evidence) =>
        Number(evidence.inspectionDocumentId ?? evidence.InspectionDocumentId)
      )
    );
    const enrichedItem = {
      ...item,
      engagementName: engagement,
      checklistName: selectedQuestion.section,
      questionCode: `${selectedQuestion.code} - ${selectedQuestion.question}`,
      clientName: client,
      evidenceFiles: files.filter((file) => evidenceIds.has(Number(file.id))),
    };
    setWorkflowResponseText(
      type === "query"
        ? ""
        : type === "finding-review"
        ? item.resolutionNotes || ""
        : item.managementResponse || ""
    );
    setWorkflowResponseAttachments([]);
    setWorkflowResponse({
      type,
      item: enrichedItem,
      task: {
        inspectionId,
        inspectionNo: reviewIdentifier,
        inspectionName: dataDetail?.name,
        engagementName: engagement,
        clientName: client,
        updatedOn: item.updatedOn,
      },
    });
  };

  const closeWorkflowResponse = () => {
    if (workflowResponseSaving) return;
    setWorkflowResponse(null);
    setWorkflowResponseText("");
    setWorkflowResponseAttachments([]);
  };

  const uploadWorkflowResponseAttachment = async (file) => {
    if (readOnly) return;
    if (!file || !inspectionId || !selectedQuestion.id || evidenceUploading)
      return;
    setEvidenceUploading(true);
    try {
      const formData = new FormData();
      formData.append("inspectionChecklistId", String(selectedQuestion.id));
      formData.append("document", file);
      formData.append("findingAttachment", "true");
      const response = await uploadInspectionEvidence(inspectionId, formData);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to upload the response attachment."
        );
      setFiles((items) => [response.data, ...items]);
      setWorkflowResponseAttachments((items) => [response.data, ...items]);
      SuccessNotify("Response attachment uploaded.");
    } catch (error) {
      ErrorNotify(
        error?.message || "Unable to upload the response attachment."
      );
    } finally {
      setEvidenceUploading(false);
    }
  };

  const stageQueryResponseAttachment = (file) => {
    if (readOnly) return;
    if (!file || workflowResponseSaving || evidenceUploading) return;
    setWorkflowResponseAttachments((items) =>
      items.some((item) => item.name === file.name && item.size === file.size)
        ? items
        : [...items, file]
    );
  };

  const downloadWorkflowQueryAttachment = async (file) => {
    try {
      await downloadInspectionDocument(
        inspectionId,
        file.id,
        file.fileName || file.name
      );
    } catch (error) {
      ErrorNotify(error?.message || "Unable to download the query attachment.");
    }
  };

  const saveWorkflowResponse = async () => {
    if (readOnly) return;
    const responseText = workflowResponseText.trim();
    if (
      !workflowResponse ||
      !inspectionId ||
      !responseText ||
      workflowResponseSaving
    )
      return;
    setWorkflowResponseSaving(true);
    try {
      const isQuery = workflowResponse.type === "query";
      const isFindingReview = workflowResponse.type === "finding-review";
      const itemId = isQuery
        ? workflowResponse.item.queryId || workflowResponse.item.id
        : workflowResponse.item.id;
      let response;
      if (isQuery) {
        if (workflowResponseAttachments.length) setEvidenceUploading(true);
        for (const file of workflowResponseAttachments) {
          const formData = new FormData();
          formData.append("attachmentType", "Response");
          formData.append("document", file);
          const uploadResponse = await uploadInspectionChecklistQueryAttachment(
            inspectionId,
            itemId,
            formData
          );
          if (!uploadResponse?.status)
            throw new Error(
              uploadResponse?.message || `${file.name} could not be uploaded.`
            );
          setWorkflowResponseAttachments((items) =>
            items.filter((item) => item !== file)
          );
        }
        setEvidenceUploading(false);
        response = await respondToInspectionChecklistQuery(
          inspectionId,
          itemId,
          { response: responseText }
        );
      } else if (isFindingReview) {
        const finding = workflowResponse.item;
        response = await updateInspectionFinding(inspectionId, itemId, {
          ...finding,
          title: finding.title,
          category: finding.category,
          severity: finding.severity,
          description: finding.description,
          recommendation: finding.recommendation,
          rootCause: finding.rootCause,
          sourceType: finding.sourceType || "ChecklistQuestion",
          checklistId: finding.checklistId,
          sectionId: finding.sectionId,
          questionId: finding.questionId,
          sourceQueryId: finding.sourceQueryId,
          ownerUserId: finding.ownerUserId,
          managementResponseRequired: finding.managementResponseRequired,
          responseRequestedFromRole: finding.responseRequestedFromRole,
          responseRequestedFromUserId: finding.responseRequestedFromUserId,
          responseDueDate: finding.responseDueDate,
          remediationRequired: finding.remediationRequired,
          closureApprovalRequired: finding.closureApprovalRequired,
          closureApproverRole: finding.closureApproverRole,
          closureApproverUserId: finding.closureApproverUserId,
          findingStatus: "Resolved",
          resolutionNotes: responseText,
          evidenceIds: (finding.evidence || [])
            .map(
              (evidence) =>
                evidence.inspectionDocumentId ?? evidence.InspectionDocumentId
            )
            .filter(Boolean),
        });
      } else {
        response = await saveInspectionFindingManagementResponse(
          inspectionId,
          itemId,
          {
            managementResponse: responseText,
            evidenceIds: workflowResponseAttachments
              .map((file) => Number(file.id))
              .filter(Boolean),
          }
        );
      }
      if (!response?.status)
        throw new Error(
          response?.message ||
            `Unable to submit the ${isQuery ? "query" : "finding"} response.`
        );
      if (isQuery) {
        const message = {
          id: response.data?.messageId || `new-${Date.now()}`,
          type: response.data?.type || "Response",
          text: responseText,
          authorName: "You",
          createdOn: new Date().toISOString(),
        };
        setCreatedQueries((items) =>
          items.map((item) =>
            (item.queryId || item.id) === itemId
              ? {
                  ...item,
                  messages: [...(item.messages || []), message],
                  finalResponse: responseText,
                  finalResponseOn: message.createdOn,
                  status: response.data?.status || "ResponseReceived",
                }
              : item
          )
        );
        setReviewQueries((items) =>
          items.map((item) =>
            (item.queryId || item.id) === itemId
              ? {
                  ...item,
                  messages: [...(item.messages || []), message],
                  finalResponse: responseText,
                  finalResponseOn: message.createdOn,
                  status: response.data?.status || "ResponseReceived",
                }
              : item
          )
        );
      } else {
        const responseMessage = {
          id: `response-${Date.now()}`,
          type: isFindingReview ? "Resolved" : "Response",
          text: responseText,
          authorName: "You",
          createdOn: new Date().toISOString(),
        };
        const workflowUpdate = isFindingReview
          ? { findingStatus: "Resolved", resolutionNotes: responseText }
          : {
              managementResponse: responseText,
              responseStatus: "ResponseReceived",
              findingStatus: "InReview",
            };
        setFindings((items) =>
          items.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  ...response.data,
                  ...workflowUpdate,
                  messages: [...(item.messages || []), responseMessage],
                }
              : item
          )
        );
        setFindingWorkspace((item) =>
          item?.id === itemId
            ? {
                ...item,
                ...response.data,
                ...workflowUpdate,
                messages: [...(item.messages || []), responseMessage],
              }
            : item
        );
      }
      SuccessNotify(
        isFindingReview
          ? "Finding resolved."
          : `${isQuery ? "Query" : "Finding"} response submitted for review.`
      );
      setWorkflowResponse(null);
      setWorkflowResponseText("");
      setWorkflowResponseAttachments([]);
    } catch (error) {
      ErrorNotify(error?.message || "Unable to submit the response.");
    } finally {
      setEvidenceUploading(false);
      setWorkflowResponseSaving(false);
    }
  };

  const resolveWorkflowQuery = async () => {
    if (readOnly) return;
    const queryId =
      workflowResponse?.item?.queryId || workflowResponse?.item?.id;
    if (
      !inspectionId ||
      !queryId ||
      workflowResponseSaving ||
      !window.confirm(
        "Resolve this query? The complete conversation will remain available in the audit history."
      )
    )
      return;
    setWorkflowResponseSaving(true);
    try {
      const response = await closeInspectionChecklistQuery(
        inspectionId,
        queryId
      );
      if (!response?.status)
        throw new Error(response?.message || "Unable to resolve the query.");
      await refreshQueryConversation(queryId);
      setWorkflowResponse(null);
      setWorkflowResponseText("");
      setWorkflowResponseAttachments([]);
      SuccessNotify("Query resolved.");
    } catch (error) {
      ErrorNotify(error?.message || "Unable to resolve the query.");
    } finally {
      setWorkflowResponseSaving(false);
    }
  };

  const toggleDrawer = (drawer) => {
    setEngagementFilesOpen(false);

    if (drawer === "findings") setFindingActionOpen(null);
    setActiveDrawer((current) =>
      drawerPinned
        ? drawer
        : current === drawer
        ? drawer === "history"
          ? questionDetailTab
          : current
        : drawer
    );
  };

  const selectQuestionTab = (tab) => {
    setEngagementFilesOpen(false);
    setDrawerPinned(false);

    if (tab === "findings") setFindingActionOpen(null);
    setActiveDrawer(tab);
    setQuestionDetailTab(tab);
  };

  const openEngagementFiles = async () => {
    setDrawerPinned(false);
    setEngagementFilesOpen(true);
    setIsLoadingEngagementFiles(true);
    try {
      if (!engagementId) {
        setEngagementFiles([]);
        return;
      }
      const [documents, folders] = await Promise.all([
        getEngagementDocuments(engagementId, inspectionId),
        getEngagementDocumentFolders(engagementId, inspectionId),
      ]);
      if (!documents?.status || !folders?.status)
        throw new Error(
          documents?.message ||
            folders?.message ||
            "Unable to load engagement files."
        );
      setEngagementFiles(documents.data || []);
      setEngagementDocumentFolders(folders.data || []);
      setSelectedEngagementDocumentFolderPath((current) =>
        folders.data?.some((folder) => folder.path === current)
          ? current
          : folders.data?.[0]?.path || ""
      );
    } catch (error) {
      setEngagementFiles([]);
      ErrorNotify(error.message || "Unable to load engagement files.");
    } finally {
      setIsLoadingEngagementFiles(false);
    }
  };

  const downloadEngagementDocument = async (file) => {
    const fileName = file?.fileName || file?.name || "document";
    if (!file?.id) {
      ErrorNotify("This document is not available for download.");
      return;
    }

    try {
      let token = "";
      token = "";
      const response = await fetch(
        `${url.BASE_URL}/engagement/downloaddocument/${
          file.id
        }?inspectionReviewId=${encodeURIComponent(inspectionId)}`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Unable to download this document.");

      const objectUrl = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      ErrorNotify(error.message || `Unable to download ${fileName}.`);
    }
  };

  const downloadEvidenceFile = async (file) => {
    const fileName = file?.fileName || file?.name || "evidence-file";
    if (!inspectionId || !file?.id) {
      ErrorNotify("This evidence file is not available for download.");
      return;
    }

    try {
      let token = "";
      token = "";
      const response = await fetch(
        `${url.BASE_URL}/inspection/${inspectionId}/evidence/${file.id}/download`,
        {
          method: "GET",
          credentials: "include",
        }
      );
      if (!response.ok)
        throw new Error("Unable to download this evidence file.");

      const objectUrl = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      ErrorNotify(error.message || `Unable to download ${fileName}.`);
    }
  };

  const deleteEvidenceFile = async (file) => {
    if (readOnly) return;
    const fileName = file?.fileName || file?.name || "this evidence file";
    if (
      !inspectionId ||
      !file?.id ||
      !window.confirm(`Delete ${fileName}? This cannot be undone.`)
    )
      return;

    setEvidenceActionOpen(null);
    setDeletingEvidenceId(file.id);
    try {
      const response = await deleteInspectionEvidence(inspectionId, file.id);
      if (!response?.status)
        throw new Error(
          response?.message || "Unable to delete the evidence file."
        );
      setFiles((items) => items.filter((item) => item.id !== file.id));
      setNewFinding((current) => ({
        ...current,
        evidenceIds: current.evidenceIds.filter((id) => id !== file.id),
      }));
      SuccessNotify(`${fileName} deleted.`);
    } catch (error) {
      ErrorNotify(error.message || "Unable to delete the evidence file.");
    } finally {
      setDeletingEvidenceId(null);
    }
  };
  const contextTools = [
    ["evidence", "mdi mdi-paperclip", "Evidence", questionEvidenceFiles.length],
    [
      "queries",
      "mdi mdi-message-question-outline",
      "Queries",
      createdQueries.length,
    ],
    ["findings", "mdi mdi-flag-outline", "Findings", questionFindings.length],
    ["history", "mdi mdi-history", "History", activities.length],
  ];
  const submissionStepIndex =
    { validating: 0, generating: 1, submitted: 2 }[submissionStage] ?? -1;
  const submissionSteps = [
    [
      "Validating review",
      "Checking questions, queries, and required findings.",
    ],
    [
      "Submitting review",
      "Saving the checklist submission and advancing the review workflow.",
    ],
    [
      "Submitting for approval",
      "Completing the submission.",
    ],
  ];

  return (
    <ReviewActivityProvider><FullScreenPageLayout
      showUserInfo
      steps={React.isValidElement(wizardSteps) ? React.cloneElement(wizardSteps, { onNavigate: requestNavigation }) : wizardSteps}
      showAskPiers={false}
      className="checklist-review-page"
      breadcrumbs={[
        ...breadcrumbItems.map((item) => ({ label: item.label, to: item.path })),
        { label: "Review Checklist" },
      ]}
      actions={<ReviewHeaderActions review={dataDetail || { id: inspectionId, engagementId }} discussionAction={discussionAction} onClose={() => setCloseReviewConfirmOpen(true)} onEngagement={() => setEngagementDetailOpen(true)} onActivities={() => toggleDrawer("history")} activityCount={activities.length} />}
    >
    {readOnly && <p role="status" className="alert alert-info m-3">Read-only · Assigned to {dataDetail?.assignedUser?.fullName || "the assigned reviewer"}. You cannot change scope, answers, evidence, queries, findings or submit this review.</p>}
    <div className="checklist-review">
      {scopePreview && <p role="status" className="alert alert-info m-3">Read-only checklist preview. No questions are in the saved review scope. Select and save the scope before answering or submitting this checklist. {allChecklistsAction && <button type="button" className="btn btn-link p-0" onClick={() => allChecklistsAction({ defineScope: true })}>Define Scope in Review Summary &rarr; Checklists</button>}</p>}
      {submissionStage && (
        <div
          className="checklist-review__submission-overlay"
          role="status"
          aria-live="polite"
          aria-label="Submitting review for approval"
        >
          <section className="checklist-review__submission-progress">
            <i
              className={`bx ${
                submissionStage === "submitted"
                  ? "bx-check-circle"
                  : "bx-loader-alt bx-spin"
              }`}
            />
            <div>
              <h2>
                {submissionStage === "submitted"
                  ? "Review submitted for approval"
                  : "Preparing approval package"}
              </h2>
              <p>
                {submissionStage === "submitted"
                  ? "The review has been submitted to the approver."
                  : "Please keep this window open while the review is processed."}
              </p>
            </div>
            <ol>
              {submissionSteps.map(([title, detail], index) => (
                <li
                  key={title}
                  className={
                    index < submissionStepIndex ||
                    submissionStage === "submitted"
                      ? "is-complete"
                      : index === submissionStepIndex
                      ? "is-active"
                      : ""
                  }
                >
                  <i
                    className={`bx ${
                      index < submissionStepIndex ||
                      submissionStage === "submitted"
                        ? "bx-check"
                        : index === submissionStepIndex
                        ? "bx-loader-alt bx-spin"
                        : "bx-circle"
                    }`}
                  />
                  <span>
                    <b>{title}</b>
                    <small>{detail}</small>
                  </span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}


      <div
        className={`checklist-review__layout ${
          drawerPinned && activeDrawer === "history" ? "has-pinned-drawer" : ""
        } ${navigatorCollapsed ? "is-navigator-collapsed" : "is-navigator-visible"}`}
      >
        <aside
          ref={questionNavigatorRef}
          id="checklist-question-navigator"
          tabIndex={-1}
          className={`checklist-review__navigator ${
            navigatorCollapsed ? "is-collapsed" : ""
          }`}
        >
          <header>
            <h2>{selectedChecklistName || "Checklists"}</h2>
            <div className="checklist-review__filter-menu">
              <button
                type="button"
                onClick={() => setNavigatorFilterOpen((value) => !value)}
                aria-label="Filter checklists"
                aria-expanded={navigatorFilterOpen}
                aria-haspopup="menu"
                title="Filter checklists"
              >
                <i className="bx bx-filter-alt" />
              </button>
              {navigatorFilterOpen && (
                <div
                  className="checklist-review__filter-options"
                  role="menu"
                  aria-label="Checklist status filter"
                >
                  {[
                    ["all", "All"],
                    ["pending", "Pending"],
                    ["completed", "Completed"],
                    ["awaiting", "Awaiting"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={navigatorFilter === value ? "active" : ""}
                      onClick={() => chooseNavigatorFilter(value)}
                      role="menuitemradio"
                      aria-checked={navigatorFilter === value}
                    >
                      <span>{label}</span>
                      {navigatorFilter === value && (
                        <i className="bx bx-check" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="checklist-review__navigator-toggle"
              onClick={() => setNavigatorCollapsed(true)}
              aria-label="Hide question navigator"
              title="Hide question navigator"
            >
              <i className="bx bx-x" aria-hidden="true" />
            </button>
          </header>
          {navigatorCollapsed ? (
            <>
              <button
                type="button"
                className="checklist-review__navigator-expand"
                onClick={() => setNavigatorCollapsed(false)}
                aria-label="Expand checklists"
                title="Expand checklists"
              >
                <i className="bx bx-chevron-right" />
              </button>
              <span className="checklist-review__navigator-collapsed-label">
                Checklists
              </span>
            </>
          ) : (
            <>
              <label className="checklist-review__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search questions..."
                />
              </label>
              <div className="checklist-review__navigator-scroll">
                <div className="checklist-review__nav-all">
                  <b>All Questions</b>
                  <span>{allQuestionCount}</span>
                </div>
                {navigatorSections.map((section) => {
                  const isCollapsed = Boolean(collapsedSections[section.id]);
                  return (
                    <section
                      className={`checklist-review__nav-section ${
                        isCollapsed ? "is-collapsed" : ""
                      }`}
                      key={section.id}
                    >
                      <button
                        className="checklist-review__nav-heading"
                        type="button"
                        onClick={() => toggleNavigatorSection(section.id)}
                        aria-expanded={!isCollapsed}
                      >
                        <i
                          className={`bx bx-chevron-${
                            isCollapsed ? "right" : "down"
                          }`}
                        />
                        <b>{section.label}</b>
                        <strong>{section.count}</strong>
                        <i
                          className={`bx bx-chevron-${
                            isCollapsed ? "down" : "up"
                          }`}
                        />
                      </button>
                      {!isCollapsed && (
                        <>
                          <span className="checklist-review__nav-progress">
                            <i style={{ width: `${section.progress}%` }} />
                          </span>
                          <div className="checklist-review__nav-question-list">
                            {section.questions.map((item) => {
                              const isSelected =
                                item.id === selectedQuestion.id;
                              const isBookmarked = bookmarkedQuestionIds.has(
                                String(item.id)
                              );
                              const querySummary = querySummaryByQuestion.get(
                                String(item.id)
                              );
                              return (
                                <button
                                  type="button"
                                  key={item.id}
                                  className={`checklist-review__question-link ${
                                    isSelected ? "active" : ""
                                  } ${isBookmarked ? "is-bookmarked" : ""}`}
                                  onClick={() => selectQuestion(item)}
                                >
                                  <i
                                    className={`bx ${statusIcon(
                                      item.status
                                    )} checklist-review__question-link-status checklist-review__question-link-status--${questionStatus(
                                      item.status
                                    )}`}
                                  />
                                  <span className="checklist-review__question-preview">
                                    {item.code} - {item.question}
                                  </span>
                                  <span className="checklist-review__question-indicators">
                                    {querySummary?.open > 0 && (
                                      <span
                                        className="checklist-review__question-query-status is-open"
                                        title={`${querySummary.open} open quer${
                                          querySummary.open === 1 ? "y" : "ies"
                                        }`}
                                        aria-label={`${
                                          querySummary.open
                                        } open quer${
                                          querySummary.open === 1 ? "y" : "ies"
                                        }`}
                                      >
                                        <i className="bx bx-message-error" />
                                        <b>{querySummary.open}</b>
                                      </span>
                                    )}
                                    {querySummary?.resolved > 0 && (
                                      <span
                                        className="checklist-review__question-query-status is-resolved"
                                        title={`${
                                          querySummary.resolved
                                        } resolved quer${
                                          querySummary.resolved === 1
                                            ? "y"
                                            : "ies"
                                        }`}
                                        aria-label={`${
                                          querySummary.resolved
                                        } resolved quer${
                                          querySummary.resolved === 1
                                            ? "y"
                                            : "ies"
                                        }`}
                                      >
                                        <i className="bx bx-message-check" />
                                        <b>{querySummary.resolved}</b>
                                      </span>
                                    )}
                                    {findings.some(
                                      (finding) =>
                                        String(finding.questionId) ===
                                        String(item.id)
                                    ) ? (
                                      <i className="bx bx-flag checklist-review__question-trailing checklist-review__question-finding" />
                                    ) : isBookmarked ? (
                                      <i className="bx bxs-bookmark checklist-review__question-trailing checklist-review__question-bookmark" />
                                    ) : isSelected ? (
                                      <i className="bx bx-chevron-right checklist-review__question-trailing" />
                                    ) : null}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {section.questions.length === 0 && (
                            <small className="checklist-review__empty-section">
                              No questions in this section.
                            </small>
                          )}
                        </>
                      )}
                    </section>
                  );
                })}
              </div>
              <footer className="checklist-review__navigator-legend">
                <span>
                  <i className="bx bx-circle" /> Not Started
                </span>
                <span>
                  <i className="bx bx-circle" /> In Progress
                </span>
                <span>
                  <i className="bx bx-check-circle" /> Reviewed
                </span>
                <span className="is-open-query">
                  <i className="bx bx-message-error" /> Open Query
                </span>
                <span className="is-resolved-query">
                  <i className="bx bx-message-check" /> Resolved Query
                </span>
                <span>
                  <i className="bx bx-paperclip" /> Attachment
                </span>
                <span>
                  <i className="bx bx-message-rounded" /> Discussion
                </span>
              </footer>
            </>
          )}
        </aside>

        <section className="checklist-review__main">
          <nav className="checklist-review__checklist-tabs" role="tablist" aria-label="Checklist workspace" hidden>
            {[["checklist", "Checklist Questions", null], ["queries", "Checklist Queries", recordsForChecklist.queries.length],
              ["findings", "Checklist Findings", recordsForChecklist.findings.length], ["documents", "Checklist Documents", checklistDocumentsLoading ? "…" : checklistDocumentsError ? "—" : checklistDocuments.length],
              ["activity", "Checklist Activity", recordsForChecklist.activities.length]].map(([id, label, count]) => (
              <button key={id} type="button" role="tab" id={`checklist-tab-${id}`} aria-selected={checklistTab === id}
                aria-controls={`checklist-panel-${id}`} onClick={() => setChecklistTab(id)}>
                {label}{count !== null && <b>{count}</b>}
              </button>
            ))}
          </nav>
          <div id="checklist-panel-checklist" role="tabpanel" aria-labelledby="checklist-tab-checklist" style={{ display: checklistTab === "checklist" ? undefined : "none" }}>
          <header className="checklist-review__main-progress">
            <span>
              {backToMyWorkAction && <button type="button" className="checklist-review__back-to-checklists" onClick={() => requestNavigation(backToMyWorkAction)}><i className="bx bx-left-arrow-alt" aria-hidden="true" /> Back to My Work</button>}
              {allChecklistsAction && <button type="button" className="checklist-review__back-to-checklists" onClick={() => requestNavigation(allChecklistsAction)}><i className="bx bx-left-arrow-alt" aria-hidden="true" /> Back to Checklists</button>}
              <button type="button" className="checklist-review__view-all-questions" aria-controls="checklist-question-navigator" aria-expanded={!navigatorCollapsed} onClick={() => {
                setNavigatorCollapsed(false); setNavigatorFilter("all"); setNavigatorFilterOpen(false); setSearch(""); setCollapsedSections({});
                requestAnimationFrame(() => questionNavigatorRef.current?.focus({ preventScroll: true }));
              }}>View all questions</button>
              {selectedChecklistName && <b className="checklist-review__current-checklist">{selectedChecklistName}</b>}
              <i className="bx bx-slider-alt" /> <b>{selectedQuestion.section}</b>
              <em>Question {selectedIndex + 1} of {questions.length}</em><i className="bx bx-info-circle" />
            </span>
            <div className="checklist-review__question-header-actions">
              <div className="checklist-review__question-code">
                <button type="button" className="checklist-review__query-count" onClick={() => { selectQuestionTab("queries"); requestAnimationFrame(() => queryHost?.scrollIntoView({ behavior: "smooth", block: "center" })); }}><i className="bx bx-message-rounded" /> {createdQueries.length} Queries</button>
                <button type="button" className="checklist-review__observation-count" onClick={() => { selectQuestionTab("findings"); requestAnimationFrame(() => findingHost?.scrollIntoView({ behavior: "smooth", block: "center" })); }}><i className="bx bx-note" /> {observationCount} Observations</button>
                <button type="button" className="checklist-review__finding-count" onClick={() => { selectQuestionTab("findings"); requestAnimationFrame(() => findingHost?.scrollIntoView({ behavior: "smooth", block: "center" })); }}><i className="bx bx-flag" /> {questionFindings.length} Findings</button>
              </div>
              <button type="button" className="checklist-review__bookmark" onClick={toggleBookmark} disabled={readOnly || bookmarkUpdating} aria-label={bookmarkedQuestionIds.has(String(selectedQuestion.id)) ? "Remove bookmark" : "Bookmark question"} title={bookmarkedQuestionIds.has(String(selectedQuestion.id)) ? "Remove bookmark" : "Bookmark question"}>
                <i className={`bx ${bookmarkedQuestionIds.has(String(selectedQuestion.id)) ? "bxs-bookmark" : "bx-bookmark"}`} />
              </button>
            </div>
          </header>

          <article className="checklist-review__question-card">
            <div className="checklist-review__question-columns">
            <div className="checklist-review__answer-column">
            <section className="review-question-summary" aria-label="Current review question">
            <h1>
              {selectedQuestion.code} - {selectedQuestion.question}
            </h1>
            {selectedQuestion.reference && (
              <em>Ref: {selectedQuestion.reference}</em>
            )}
            {selectedQuestion.answerType !== "None" && (isChoiceAnswer ? <section className="checklist-review__response" aria-label="Assessment options">
              <div>
                {Object.entries(responseLabels).map(([value, label]) => (
                  <button
                    className={
                      responseValue === value
                        ? `selected selected--${value}`
                        : ""
                    }
                    type="button"
                    key={value}
                    onClick={() => chooseAnswer(value)}
                    disabled={!isChecklistReviewerTask || responseSaving}
                  >
                    <i
                      className={`bx ${
                        value === "yes"
                          ? "bx-check"
                          : value === "no"
                          ? "bx-x"
                          : value === "na"
                          ? "bx-text"
                          : "bx-question-mark"
                      }`}
                    />
                    <b>{label}</b>
                  </button>
                ))}
              </div>
            </section> : (
              <label className="checklist-review__typed-answer">
                Answer
                {selectedQuestion.answerType === "Long text" ? (
                  <textarea aria-label="Answer" value={answer} maxLength={4000} disabled={!isChecklistReviewerTask || responseSaving} onChange={(event) => chooseAnswer(event.target.value)} />
                ) : (
                  <input aria-label="Answer" type={selectedQuestion.answerType === "Number" ? "number" : selectedQuestion.answerType === "Date" ? "date" : "text"} step={selectedQuestion.answerType === "Number" ? "any" : undefined} value={answer} maxLength={4000} disabled={!isChecklistReviewerTask || responseSaving} onChange={(event) => chooseAnswer(event.target.value)} />
                )}
              </label>
            ))}
                  </section>
                  </div>
                  <div className="checklist-review__support-stack checklist-review__support-tabs">
                    <ReviewActivity actions={<>
                      {isChecklistReviewerTask && <button type="button" disabled={responseSaving} onClick={() => { closeActivityForms(); evidenceReferencesRef.current?.open(); }}><i className="bx bx-paperclip" /> Evidence</button>}
                      {canRaiseFinding && <button type="button" onClick={() => { closeActivityForms(); selectQuestionTab("queries"); setNewQueryFormOpen(true); }}><i className="bx bx-message-rounded-dots" /> Query</button>}
                      {!readOnly && <button type="button" onClick={() => { closeActivityForms(); setCommentCreate(value => value + 1); }}><i className="bx bx-comment" /> Comment</button>}
                      {canRaiseFinding && <><button type="button" onClick={() => { closeActivityForms(); selectQuestionTab("findings"); setObservationCreate(value => value + 1); }}><i className="bx bx-note" /> Observation</button><button type="button" onClick={openFindingForm}><i className="bx bx-flag" /> Finding</button></>}
                    </>}>
                      {queriesRefreshError && <p role="alert">{queriesRefreshError} <button type="button" disabled={queriesRefreshing} onClick={refreshQueriesSection}>Retry queries</button></p>}
<section id="question-evidence" className="question-support-panel checklist-review__evidence-workspace checklist-review__inline-evidence">
                      <EvidenceReferences ref={evidenceReferencesRef} onDirty={setEvidenceDirty} disabled={responseSaving} key={selectedQuestion.id} inspectionId={inspectionId} questionId={selectedQuestion.id} files={questionEvidenceFiles} canEdit={isChecklistReviewerTask} onSaved={(file) => setFiles((items) => [file, ...items])} onUpdated={(file) => setFiles((items) => items.map((item) => item.id === file.id ? { ...item, ...file } : item))} onRefresh={async () => {
                        const response = await getInspectionEvidence(inspectionId, selectedQuestion.id, true);
                        if (!response?.status || !Array.isArray(response.data)) throw new Error(response?.message || "Unable to refresh evidence.");
                        setFiles(response.data);
                      }} onDelete={deleteEvidenceFile} onDownload={downloadEvidenceFile} deletingId={deletingEvidenceId} />
</section>
<div id="question-queries" className="question-support-panel checklist-review__embedded-support-panel is-queries" ref={setQueryHost} />
<div id="question-findings" className="question-support-panel">
  <div className="checklist-review__embedded-support-panel is-findings" ref={setFindingHost} />

</div>
                    <Discussion key={selectedQuestion.id} readOnly={readOnly} createRequest={commentCreate} inspectionId={inspectionId} inspectionChecklistId={selectedQuestion.id} onDownload={downloadEvidenceFile} />
                    </ReviewActivity>
                  </div>
            </div>
            {isChecklistSignOffTask ? (
              <section className="checklist-review__sign-off-comments">
                <header>
                  <div>
                    <h2>Sign-off Comments</h2>
                    <p className="checklist-review__section-description">
                      Optional comments for this question. Add feedback before
                      re-opening it for review.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={saveSignOffComments}
                    disabled={signOffCommentSaving}
                  >
                    <i
                      className={`bx ${
                        signOffCommentSaving
                          ? "bx-loader-alt bx-spin"
                          : "bx-save"
                      }`}
                    />{" "}
                    {signOffCommentSaving ? "Saving…" : "Save comments"}
                  </button>
                </header>
                <textarea
                  value={signOffComments}
                  maxLength="4000"
                  onChange={(event) => setSignOffComments(event.target.value)}
                  placeholder="Add sign-off comments or feedback for the reviewer..."
                />
                <footer>
                  <span>{signOffComments.length} / 4000</span>
                  <button
                    className="is-reopen"
                    type="button"
                    onClick={() => setReopenConfirmOpen(true)}
                    disabled={!signOffComments.trim() || signOffCommentSaving}
                  >
                    <i className="bx bx-undo" /> Re-open for Review
                  </button>
                </footer>
              </section>
            ) : (
              selectedQuestion.signOffComments && (
                <section className="checklist-review__sign-off-feedback">
                  <i className="bx bx-message-square-detail" />
                  <div>
                    <h2>Sign-off Feedback</h2>
                    <p>{selectedQuestion.signOffComments}</p>
                  </div>
                </section>
              )
            )}
          </article>
          <footer className="checklist-review__main-footer">
            <button
              type="button"
              onClick={() => moveQuestion(-1)}
              disabled={selectedIndex === 0 || responseSaving}
            >
              <i className="bx bx-left-arrow-alt" /> Previous Question
            </button>
            <span hidden role="status" aria-live="polite" className={saved && !saveFailed && selectedQuestion.status === "Answered" ? "saved" : "unsaved"}>
              <i className={`bx ${saved && !saveFailed && selectedQuestion.status === "Answered" ? "bx-check" : "bx-time"}`} />{" "}
              {responseSaving ? "Saving…" : saveFailed ? "Save failed — changes retained" : !saved ? "Unsaved changes" : selectedQuestion.status === "Answered" ? "Saved" : "Not answered"}
            </span>
            {isChecklistReviewerTask && (
              <button hidden type="button" onClick={save} disabled={responseSaving || (saved && selectedQuestion.status === "Answered" && !saveFailed)}>
                <i
                  className={`bx ${
                    responseSaving ? "bx-loader-alt bx-spin" : "bx-save"
                  }`}
                />{" "}
                {responseSaving ? "Saving…" : "Save"}
              </button>
            )}
            {selectedIndex === questions.length - 1 ? (
              readOnly ? null : (
              openReviewQueries.length ? (
                <button
                  className="next"
                  type="button"
                  onClick={openOutstandingQueries}
                  disabled={responseSaving || Boolean(submissionStage) || !questions.length || !workflowTaskActive}
                >
                  View Open Queries ({openReviewQueries.length}){" "}
                  <i className="bx bx-message-rounded-dots" />
                </button>
              ) : (
                <button
                  className="next"
                  type="button"
                  onClick={
                    !workflowTaskActive && workflowTask?.workflowStatus === "Completed"
                      ? () => setSubmittedInfoOpen(true)
                      : workflowTask ? completeWorkflowTask : submitForApproval
                  }
                  disabled={responseSaving || Boolean(submissionStage) || !questions.length || (!workflowTaskActive && workflowTask?.workflowStatus !== "Completed")}
                >
                  {responseSaving
                    ? "Saving…"
                    : !workflowTaskActive
                    ? workflowTask?.workflowStatus === "Completed" ? "Checklist Submitted" : "Awaiting Review Assignment"
                    : workflowTask
                    ? workflowTaskLabel
                    : "Submit for Approval"}{" "}
                  <i className="bx bx-right-arrow-alt" />
                </button>
              ))
            ) : (
              <button
                className="next"
                type="button"
                onClick={() => moveQuestion(1)}
                disabled={responseSaving || !questions.length}
              >
                Next Question <i className="bx bx-right-arrow-alt" />
              </button>
            )}
          </footer>
          {isChecklistReviewerTask && <p hidden className="px-3"><small>Save your changes here, or use Save &amp; Next to save and continue.</small></p>}
          </div>
          {checklistTab !== "checklist" && (
            <section className="checklist-review__checklist-records" role="tabpanel" id={`checklist-panel-${checklistTab}`} aria-labelledby={`checklist-tab-${checklistTab}`}>
              <h2>{({ queries: "Queries", findings: "Findings", documents: "Documents", activity: "Activity", reports: "Reports" })[checklistTab]}</h2>
              <p>
                {checklistTab === "reports" ? <>All generated reports for this review of <strong>{engagement}</strong>, across all checklists.</> : <>
                {({ queries: "These queries", findings: "These findings", documents: "These documents", activity: "These activities" })[checklistTab]}
                {" belong to "}<strong>{selectedChecklistName || "the current checklist"}</strong>
                {" for engagement "}<strong>{engagement}</strong>.
                </>}
              </p>
              {checklistTab === "queries" && <nav className="checklist-review__query-quick-filters" aria-label="Filter checklist queries by status">
                <label className="findings-tab__search checklist-review__query-search">
                  <i className="bx bx-search" aria-hidden="true" />
                  <input aria-label="Search checklist queries" placeholder="Search queries..." value={checklistQuerySearch} onChange={(event) => setChecklistQuerySearch(event.target.value)} />
                </label>
                {queryQuickFilterOptions.map((status) => (
                  <button type="button" key={status} className={checklistQueryFilter === status ? "active" : ""} aria-pressed={checklistQueryFilter === status} onClick={() => setChecklistQueryFilter(status)}>
                    {status} <b>{recordsForChecklist.queries.filter((query) => status === "Review Required" ? query.reviewRequired === true : status === "Reply Required" ? query.replyRequired === true : status === "All" || queryWorkspaceStatus(query.status) === status).length}</b>
                  </button>
                ))}
              </nav>}
              {checklistTab === "findings" ? (
                <FindingsTab tableOnly showFilters findings={recordsForChecklist.findings}
                  actionOpen={findingActionOpen} onToggleAction={setFindingActionOpen}
                  onOpen={openFindingWorkspace} onEdit={readOnly ? undefined : openFindingForEdit} saving={findingSaving} />
              ) : checklistTab === "documents" && checklistDocumentsLoading ? <p role="status">Loading documents…</p> : checklistTab === "documents" && checklistDocumentsError ? <p role="alert">{checklistDocumentsError}</p> : (
                <CustomDataGrid showToolbar={false} initialPageSize={25}
                  gridSx={{ '&& .MuiDataGrid-cell[data-field="actions"], && .MuiDataGrid-cell[data-field="record"], && .MuiDataGrid-cell[data-field="status"], && .MuiDataGrid-cell[data-field="description"]': { display: "flex", alignItems: "center", lineHeight: "normal" }, '&& .checklist-review__query-badge': { lineHeight: "18px", height: "auto", alignSelf: "center" } }}
                  recordLabel={checklistTab} fileName={`checklist-${checklistTab}`}
                  data={checklistTab === "documents" ? checklistDocuments : checklistTab === "activity" ? recordsForChecklist.activities : checklistTab === "queries" ? recordsForChecklist.queries.filter((query) => (checklistQueryFilter === "Review Required" ? query.reviewRequired === true : checklistQueryFilter === "Reply Required" ? query.replyRequired === true : checklistQueryFilter === "All" || queryWorkspaceStatus(query.status) === checklistQueryFilter) && `${query.taskNumber || query.queryNumber || query.id} ${query.subject || ""} ${query.details || ""} ${query.raisedByName || ""}`.toLowerCase().includes(checklistQuerySearch.trim().toLowerCase())) : recordsForChecklist[checklistTab]}
                  columns={checklistTab === "queries" ? [
                    { id: "record", header: "ID", accessorFn: (query) => query.taskNumber || query.queryNumber || query.id, flex: .7,
                      cell: ({ getValue }) => <span className="checklist-review__query-badge">{getValue()}</span> },
                    { id: "description", header: "Query Title / Description", accessorFn: (query) => query.subject || "Untitled query", flex: 2.5,
                      cell: ({ row }) => <div className="checklist-review__query-title"><strong>{row.original.subject || "Untitled query"}</strong><small>{row.original.details || ""}</small></div> },
                    { id: "raisedBy", header: "Raised By", accessorFn: (query) => query.raisedByName || "—", flex: 1.3 },
                    { id: "status", header: "Status", accessorFn: (query) => queryWorkspaceStatus(query.status), flex: 1.1,
                      cell: ({ getValue }) => <span className="checklist-review__query-badge">{getValue()}</span> },
                    { id: "responses", header: "Responses", accessorFn: (query) => query.responseCount ?? query.responses?.length ?? 0, flex: .7 },
                    { id: "updated", header: "Last Updated", accessorFn: (query) => query.updatedOn || query.createdOn, flex: 1.2,
                      cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString("en-GB") : "—" },
                    { id: "actions", header: "", accessorFn: () => "", size: 55, enableSorting: false, enableColumnFilter: false, cell: ({ row }) => renderQueryActions(row.original) },
                  ] : checklistTab === "activity" ? [
                    { id: "note", header: "Activity", accessorFn: (a) => a.activityNote || "—", flex: 3 },
                    { id: "by", header: "By", accessorFn: (a) => a.userProfile?.fullName || "—" },
                    { id: "date", header: "Date", accessorFn: (a) => a.activityDate ? new Date(a.activityDate).toLocaleString() : "—" },
                  ] : [
                    { id: "record", header: checklistTab === "documents" ? "Document" : "Reference", accessorFn: (r) => checklistTab === "documents" ? r.fileName || r.name : r.findingNumber || r.taskNumber || r.queryNumber || r.id || r.queryId, flex: 1.2,
                      cell: ({ getValue }) => checklistTab === "queries" ? <span className="checklist-review__query-badge">{getValue()}</span> : getValue() },
                    { id: "question", header: "Question", accessorFn: (r) => activeChecklistQuestions.find((q) => String(q.id) === String(r.questionId || r.inspectionChecklistId))?.code || "—", flex: 0.5 },
                    { id: "description", header: "Description", accessorFn: (r) => r.subject || r.title || r.description || "—", flex: 2 },
                    { id: "status", header: "Status", accessorFn: (r) => r.findingStatus || r.status || "—", flex: 0.7,
                      cell: ({ getValue }) => checklistTab === "queries" ? <span className="checklist-review__query-badge">{queryWorkspaceStatus(getValue())}</span> : getValue() },
                    { id: "actions", header: "Action", accessorFn: () => "", enableSorting: false, enableColumnFilter: false,
                      cell: ({ row }) => checklistTab === "queries" ? renderQueryActions(row.original) : <button type="button" className="checklist-review__record-open" onClick={() => {
                        if (checklistTab === "queries") openQuery(row.original, "view");
                        else if (checklistTab === "findings") openFindingWorkspace(row.original);
                        else downloadEvidenceFile(row.original);
                      }}>{checklistTab === "documents" ? "Download" : "View"}</button> },
                  ]}
                  emptyState={{ title: "No records for this checklist", description: "Records linked to its questions will appear here." }}
                />
              )}
            </section>
          )}
        </section>

        <Modal centered isOpen={Boolean(pendingNavigation)} toggle={() => { if (!responseSaving) setPendingNavigation(null); }} labelledBy="unsaved-question-title">
          <ModalBody className="checklist-review__reopen-modal">
            <h2 id="unsaved-question-title">Save your changes?</h2>
            <p>Your answer or evidence has unsaved changes. Save them before continuing, or discard them.</p>
            <footer>
              <button type="button" disabled={responseSaving} onClick={() => setPendingNavigation(null)}>Keep editing</button>
              <button type="button" disabled={responseSaving} onClick={() => {
                const action = pendingNavigation;
                setPendingNavigation(null);
                setEvidenceDirty(false);
                setSaveFailed(false);
                action();
              }}>Discard changes</button>
              <button type="button" className="is-primary" disabled={responseSaving} onClick={async () => {
                const action = pendingNavigation;
                if (await save()) { setPendingNavigation(null); action(); }
              }}>{responseSaving ? "Saving…" : "Save and continue"}</button>
            </footer>
          </ModalBody>
        </Modal>
        <Modal
          centered
          isOpen={closeReviewConfirmOpen}
          toggle={() => setCloseReviewConfirmOpen(false)}
          labelledBy="close-review-confirm-title"
          aria-describedby="close-review-confirm-description"
        >
          <ModalBody className="checklist-review__reopen-modal">
            <h2 id="close-review-confirm-title">Close review?</h2>
            <p id="close-review-confirm-description">Are you sure you want to close the review?</p>
            <footer>
              <button type="button" autoFocus onClick={() => setCloseReviewConfirmOpen(false)}>
                Keep reviewing
              </button>
              <button type="button" className="is-primary" onClick={() => {
                setCloseReviewConfirmOpen(false);
                requestNavigation(closeAction);
              }}>
                Close review
              </button>
            </footer>
          </ModalBody>
        </Modal>
        <Modal
          centered
          isOpen={!readOnly && reopenConfirmOpen}
          toggle={() => !signOffCommentSaving && setReopenConfirmOpen(false)}
        >
          <ModalBody className="checklist-review__reopen-modal">
            <h2>Re-open this question for review?</h2>
            <p>
              This will return <b>{selectedQuestion.code}</b> to the original
              reviewer. You will remain in this sign-off checklist and can
              continue reviewing the other questions.
            </p>
            <section>
              <b>Feedback to reviewer</b>
              <p>{signOffComments}</p>
            </section>
            <footer>
              <button
                type="button"
                onClick={() => setReopenConfirmOpen(false)}
                disabled={signOffCommentSaving}
              >
                Cancel
              </button>
              <button
                className="is-primary"
                type="button"
                onClick={reopenForReview}
                disabled={signOffCommentSaving}
              >
                {signOffCommentSaving ? "Re-opening…" : "Re-open for Review"}
              </button>
            </footer>
          </ModalBody>
        </Modal>
        <Modal
          centered
          size="xl"
          isOpen={!readOnly && evidenceUploadOpen}
          toggle={discardEvidenceUpload}
          backdrop={evidenceUploading ? "static" : true}
          keyboard={!evidenceUploading}
          className="checklist-review__evidence-upload-modal"
        >
          <ModalBody className="checklist-review__evidence-modal-body">
            <form onSubmit={uploadEvidenceWithDescription}>
              <header className="checklist-review__evidence-modal-header">
                <span>
                  <i className="bx bx-cloud-upload" />
                  <b>Upload Evidence</b>
                  <small>
                    Add supporting documents for this checklist item
                  </small>
                </span>
                <button
                  type="button"
                  onClick={discardEvidenceUpload}
                  disabled={evidenceUploading}
                  aria-label="Close upload evidence"
                >
                  <i className="bx bx-x" />
                </button>
              </header>
              <section
                className="checklist-review__evidence-modal-context"
                aria-label="Evidence context"
              >
                <div>
                  <small>Engagement</small>
                  <b>{engagement}</b>
                </div>
                <div>
                  <small>Client</small>
                  <b>{client}</b>
                </div>
                <div>
                  <small>Checklist</small>
                  <b>{selectedQuestion.section}</b>
                </div>
                <div>
                  <small>Question</small>
                  <b>{selectedQuestion.code}</b>
                  <span>{selectedQuestion.question}</span>
                </div>
              </section>
              <div className="checklist-review__evidence-modal-layout">
                <div className="checklist-review__evidence-modal-main">
                  <section className="checklist-review__evidence-file-picker">
                    <b>Select files</b>
                    <input
                      ref={evidenceUploadRef}
                      type="file"
                      hidden
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                      disabled={evidenceUploading}
                      onChange={stageEvidenceUploadFiles}
                    />
                    <button
                      className={evidenceDragActive ? "is-dragging" : ""}
                      type="button"
                      onClick={() => evidenceUploadRef.current?.click()}
                      onDragEnter={(event) => {
                        event.preventDefault();
                        setEvidenceDragActive(true);
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      onDragLeave={(event) => {
                        event.preventDefault();
                        setEvidenceDragActive(false);
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        setEvidenceDragActive(false);
                        addEvidenceUploadFiles(event.dataTransfer.files);
                      }}
                      disabled={evidenceUploading}
                    >
                      <i className="bx bx-cloud-upload" />
                      <strong>
                        Drag and drop files here, or <u>click to browse</u>
                      </strong>
                      <small>
                        Supported file types: PDF, DOC, DOCX, XLS, XLSX, PPT,
                        PPTX, PNG, JPG, JPEG
                        <br />
                        Maximum file size: <b>50 MB per file</b>
                      </small>
                    </button>
                    {evidenceUploadFiles.length > 0 && (
                      <div className="checklist-review__evidence-staged-files">
                        {evidenceUploadFiles.map((file) => {
                          const extension =
                            file.name.split(".").pop()?.toUpperCase() || "FILE";
                          const fileTone = ["XLS", "XLSX"].includes(extension)
                            ? "excel"
                            : ["DOC", "DOCX"].includes(extension)
                            ? "word"
                            : ["PPT", "PPTX"].includes(extension)
                            ? "powerpoint"
                            : extension === "PDF"
                            ? "pdf"
                            : "file";
                          return (
                            <article key={`${file.name}-${file.size}`}>
                              <i
                                className={`checklist-review__evidence-type-icon is-${fileTone}`}
                              >
                                {extension.slice(
                                  0,
                                  fileTone === "powerpoint" ? 1 : 3
                                )}
                              </i>
                              <span>
                                <b>{file.name}</b>
                                <small>
                                  {file.size >= 1024 * 1024
                                    ? `${(file.size / (1024 * 1024)).toFixed(
                                        1
                                      )} MB`
                                    : `${Math.max(
                                        1,
                                        Math.round(file.size / 1024)
                                      )} KB`}
                                </small>
                              </span>
                              <em>
                                <i className="bx bx-check-circle" /> Ready to
                                upload
                              </em>
                              <button
                                type="button"
                                onClick={() =>
                                  setEvidenceUploadFiles((items) =>
                                    items.filter((item) => item !== file)
                                  )
                                }
                                disabled={evidenceUploading}
                                aria-label={`Remove ${file.name}`}
                              >
                                <i className="bx bx-trash" />
                              </button>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </section>
                  <section className="checklist-review__evidence-metadata">
                    <div>
                      <label>
                        <b>
                          Title <em>*</em>
                        </b>
                        <input
                          value={evidenceTitle}
                          maxLength="200"
                          onChange={(event) =>
                            setEvidenceTitle(event.target.value)
                          }
                          placeholder="Enter an evidence title"
                          disabled={evidenceUploading}
                        />
                      </label>
                      <label>
                        <b>
                          Evidence Type <em>*</em>
                        </b>
                        <select
                          value={evidenceType}
                          onChange={(event) =>
                            setEvidenceType(event.target.value)
                          }
                          disabled={evidenceUploading}
                        >
                          <option>Quality Review Documentation</option>
                          <option>Management Response</option>
                          <option>Supporting Data</option>
                          <option>Process Documentation</option>
                          <option>Other</option>
                        </select>
                      </label>
                    </div>
                    <label>
                      <b>
                        Description / Notes <em>*</em>
                      </b>
                      <textarea
                        rows="4"
                        required
                        maxLength="1000"
                        value={evidenceDescription}
                        onChange={(event) =>
                          setEvidenceDescription(event.target.value)
                        }
                        placeholder="Describe what this evidence supports"
                        disabled={evidenceUploading}
                      />
                      <small>{evidenceDescription.length}/1000</small>
                    </label>
                  </section>
                </div>
                <aside className="checklist-review__evidence-guidance">
                  <section>
                    <header>
                      <i className="bx bx-info-circle" />
                      <b>Upload Guidelines</b>
                    </header>
                    <ul>
                      <li>
                        Upload relevant documents that support your answer to
                        this question.
                      </li>
                      <li>
                        Ensure files are legible and not password protected.
                      </li>
                      <li>Include versioned documents where applicable.</li>
                      <li>
                        Add a clear description to help reviewers understand the
                        context.
                      </li>
                    </ul>
                  </section>
                  <section>
                    <header>
                      <i className="bx bx-file" />
                      <b>File Requirements</b>
                    </header>
                    <dl>
                      <div>
                        <dt>Maximum file size:</dt>
                        <dd>50 MB per file</dd>
                      </div>
                      <div>
                        <dt>Accepted formats:</dt>
                        <dd>
                          PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, PNG, JPG, JPEG
                        </dd>
                      </div>
                      <div>
                        <dt>Multiple files:</dt>
                        <dd>You can upload multiple files at once</dd>
                      </div>
                    </dl>
                  </section>
                  <section>
                    <header>
                      <i className="bx bx-check-circle" />
                      <b>Evidence Status</b>
                    </header>
                    <p>
                      <i className="is-complete" /> Upload complete - file is
                      ready for review
                    </p>
                    <p>
                      <i className="is-processing" /> Processing - file is being
                      uploaded
                    </p>
                    <p>
                      <i className="is-failed" /> Upload failed - please try
                      again
                    </p>
                  </section>
                </aside>
              </div>
              <footer className="checklist-review__evidence-modal-footer">
                <button
                  type="button"
                  onClick={discardEvidenceUpload}
                  disabled={evidenceUploading}
                >
                  Cancel
                </button>
                <button
                  className="is-primary"
                  type="submit"
                  disabled={
                    evidenceUploading ||
                    !evidenceUploadFiles.length ||
                    !evidenceTitle.trim() ||
                    !evidenceType ||
                    !evidenceDescription.trim()
                  }
                >
                  <i
                    className={`bx ${
                      evidenceUploading ? "bx-loader-alt bx-spin" : "bx-upload"
                    }`}
                  />{" "}
                  {evidenceUploading ? "Uploading…" : "Upload Evidence"}
                </button>
              </footer>
            </form>
          </ModalBody>
        </Modal>
        {engagementFilesOpen && (
          <>
            <button
              className="checklist-review__drawer-backdrop checklist-review__engagement-files-drawer-backdrop"
              type="button"
              aria-label="Close documents and workpapers"
              onClick={() => setEngagementFilesOpen(false)}
            />
            <aside
              className="checklist-review__drawer checklist-review__engagement-files-drawer checklist-review__engagement-files-drawer--left"
              aria-label="Documents and Workpapers drawer"
            >
              <header className="checklist-review__drawer-header">
                <span>
                  <i className="mdi mdi-paperclip" />
                  <b>Documents &amp; Workpapers</b>
                </span>
                <button
                  type="button"
                  onClick={() => setEngagementFilesOpen(false)}
                  aria-label="Close documents and workpapers"
                  className="checklist-review__drawer-close"
                >
                  <i className="bx bx-x" aria-hidden="true" /> Close
                </button>
              </header>
              <section
                className="checklist-review__document-engagement-context"
                aria-label="Document workspace context"
              >
                <div>
                  <small>Engagement</small>
                  <b>{engagement}</b>
                </div>
                <div>
                  <small>Client</small>
                  <b>{client || "—"}</b>
                </div>
              </section>
              {isLoadingEngagementFiles ? (
                <div className="checklist-review__engagement-files-empty">
                  <span className="spinner-border spinner-border-sm" /> Loading
                  documents and workpapers…
                </div>
              ) : (
                <div className="checklist-review__documents-workspace">
                  <aside className="checklist-review__document-folder-tree">
                    <header>
                      <h2>Folder Structure</h2>
                    </header>
                    <label className="checklist-review__document-folder-search">
                      <i className="bx bx-search" />
                      <input
                        type="search"
                        value={documentFolderSearch}
                        onChange={(event) =>
                          setDocumentFolderSearch(event.target.value)
                        }
                        placeholder="Search folders"
                        aria-label="Search document folders"
                      />
                    </label>
                    <div className="checklist-review__document-folder-root">
                      <i className="bx bx-folder-open" /> {engagement}
                    </div>
                    <div className="checklist-review__document-folder-list">
                      {visibleEngagementDocumentFolders.map((folder) => {
                        const isSelected =
                          selectedEngagementDocumentFolder?.path ===
                          folder.path;
                        const depth = folder.path
                          .split("/")
                          .filter(Boolean).length;
                        return (
                          <button
                            type="button"
                            key={folder.path}
                            className={isSelected ? "is-selected" : ""}
                            onClick={() =>
                              setSelectedEngagementDocumentFolderPath(
                                folder.path
                              )
                            }
                            style={{ paddingLeft: `${14 + depth * 14}px` }}
                          >
                            <i
                              className={
                                isSelected
                                  ? "bx bx-folder-open"
                                  : "bx bx-folder"
                              }
                            />{" "}
                            {folder.name}
                          </button>
                        );
                      })}
                    </div>
                  </aside>
                  <section className="checklist-review__document-folder-contents">
                    <header>
                      Folder Contents:{" "}
                      {selectedEngagementDocumentFolder?.name || "Documents"}
                    </header>
                    <div className="checklist-review__engagement-document-header">
                      <span>Name</span>
                      <span>Type</span>
                      <span>Uploaded By</span>
                      <span>Date Modified</span>
                      <span />
                    </div>
                    {visibleEngagementFiles.length ? (
                      visibleEngagementFiles.map((file, index) => {
                        const fileName =
                          file.fileName ||
                          file.name ||
                          `Engagement file ${index + 1}`;
                        const extension =
                          fileName.split(".").pop()?.toUpperCase() ||
                          file.type ||
                          "FILE";
                        const uploadedBy =
                          file.userProfile?.fullName ||
                          file.userProfile?.userName ||
                          "Engagement team";
                        const modified = file.updatedOn
                          ? new Date(file.updatedOn).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : file.size || "—";
                        return (
                          <article
                            className="checklist-review__engagement-document-row"
                            key={file.id || `${fileName}-${index}`}
                          >
                            <span>
                              <i
                                className={`bx ${getDocumentIcon(fileName)}`}
                              />
                              <b>{fileName}</b>
                            </span>
                            <span>{extension}</span>
                            <span>{uploadedBy}</span>
                            <span>{modified}</span>
                            <button
                              className="checklist-review__document-download"
                              type="button"
                              disabled={!file.id}
                              onClick={() => downloadEngagementDocument(file)}
                              aria-label={`Download ${fileName}`}
                              title={`Download ${fileName}`}
                            >
                              <i className="bx bx-download" /> Download
                            </button>
                          </article>
                        );
                      })
                    ) : (
                      <div className="checklist-review__engagement-files-empty">
                        <i className="mdi mdi-folder-open-outline" />
                        <b>No documents in this folder</b>
                        <p>
                          Select another folder or upload documents from the
                          Engagement workspace.
                        </p>
                      </div>
                    )}
                  </section>
                </div>
              )}
            </aside>
          </>
        )}
        {["queries", "findings", ...(activeDrawer === "history" ? ["history"] : [])].map((panelDrawer) => {
            const isHistoryDrawer = panelDrawer === "history";
            const panel = (
              <aside
                className={
                  isHistoryDrawer
                    ? `checklist-review__drawer checklist-review__context-drawer ${
                        drawerPinned ? "is-pinned" : ""
                      }`
                    : "checklist-review__drawer checklist-review__context-tabs-panel"
                }
                aria-label={`${
                  contextTools.find(([id]) => id === panelDrawer)?.[2]
                } ${isHistoryDrawer ? "drawer" : "activity controls"}`}
              >
                {isHistoryDrawer && (
                  <header className="checklist-review__drawer-header">
                    <span>
                      <i
                        className={
                          contextTools.find(([id]) => id === panelDrawer)?.[1]
                        }
                      />
                      <b>
                        {contextTools.find(([id]) => id === panelDrawer)?.[2]}
                      </b>
                    </span>
                    <button
                      className={drawerPinned ? "is-active" : ""}
                      type="button"
                      onClick={() => setDrawerPinned((value) => !value)}
                      aria-label={drawerPinned ? "Unpin drawer" : "Pin drawer"}
                      aria-pressed={drawerPinned}
                    >
                      <i
                        className={`bx bx-pin ${
                          drawerPinned ? "is-pinned" : "is-unpinned"
                        }`}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDrawerPinned(false);
                        setActiveDrawer(questionDetailTab);
                      }}
                      aria-label="Close drawer"
                      className="checklist-review__drawer-close"
                    >
                      <i className="bx bx-x" aria-hidden="true" /> Close
                    </button>
                  </header>
                )}
                {["evidence", "queries", "findings"].includes(panelDrawer) && (
                  <section
                    className="checklist-review__drawer-question-context"
                    aria-label="Question context"
                  >
                    <div>
                      <small>Checklist</small>
                      <b>
                        {selectedQuestion.section || "Unassigned checklist"}
                      </b>
                    </div>
                    <div>
                      <small>Question</small>
                      <strong>
                        {selectedQuestion.code} - {selectedQuestion.question}
                      </strong>
                    </div>
                  </section>
                )}
                <div
                  className={`checklist-review__context ${
                    (panelDrawer === "queries" && !queryMode) ||
                    (panelDrawer === "findings" && !findingMode) ||
                    panelDrawer === "evidence"
                      ? "is-query-actions-visible"
                      : ""
                  }`}
                >
                  {panelDrawer === "queries" &&
                    (queryMode ? (
                      <section
                        className="checklist-review__finding-form checklist-review__query-form is-popup"
                        role="dialog"
                        aria-modal="true"
                        aria-label={
                          queryMode === "view"
                            ? "Query details"
                            : queryMode === "edit"
                            ? "Edit query"
                            : "New query"
                        }
                      >
                        <header className="checklist-review__query-drawer-heading">
                          <span>
                            <i className="bx bx-message-rounded-dots" />
                            <b>
                              {queryMode === "view"
                                ? "Query details"
                                : queryMode === "edit"
                                ? "Edit Query"
                                : "New Query"}
                            </b>
                            <small>
                              {queryMode === "view"
                                ? "Review the query and its assignment"
                                : "Raise a query related to this question"}
                            </small>
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedQuery(null);
                              setQueryMode(null);
                              setQueryAttachmentFiles([]);
                            }}
                            disabled={querySaving}
                            aria-label="Close query drawer"
                          >
                            <i className="bx bx-x" />
                          </button>
                        </header>
                        <div className="checklist-review__query-create-layout">
                          <div className="checklist-review__query-create-main">
                            {newQuery.aiActionId && (
                              <p role="status">
                                AI generated - review before creating.
                              </p>
                            )}
                            <div className="checklist-review__query-field-row">
                            <label>
                              <b>Query Type</b>
                              <span className="checklist-review__query-select">
                                <i className="bx bx-file" />
                                <select
                                  value={newQuery.queryType}
                                  onChange={(event) =>
                                    setNewQuery((current) => ({
                                      ...current,
                                      queryType: event.target.value,
                                    }))
                                  }
                                >
                                  <option>Clarification</option>
                                  <option>Evidence Request</option>
                                  <option>Follow-up</option>
                                  <option>Other</option>
                                </select>
                              </span>
                            </label>
                            <label>
                              <b>
                                Assign To <em>*</em>
                              </b>
                              <span className="checklist-review__query-select checklist-review__query-assignee">
                                <i className="bx bx-user" />
                                <Select
                                  inputId="query-assignee"
                                  className="checklist-review__query-assignee-select"
                                  classNamePrefix="checklist-review-query-assignee"
                                  isSearchable
                                  isClearable
                                  isDisabled={!queryAssignees.length}
                                  options={queryAssignees.map((user) => ({
                                    value: user.id,
                                    label: user.name,
                                  }))}
                                  value={
                                    queryAssignees
                                      .map((user) => ({
                                        value: user.id,
                                        label: user.name,
                                      }))
                                      .find(
                                        (option) =>
                                          String(option.value) ===
                                          String(newQuery.assigneeId)
                                      ) || null
                                  }
                                  onChange={(option) =>
                                    setNewQuery((current) => ({
                                      ...current,
                                      assigneeId: option?.value || "",
                                    }))
                                  }
                                  placeholder={
                                    queryAssignees.length
                                      ? "Search and select an assignee"
                                      : "Loading assignees..."
                                  }
                                  noOptionsMessage={() =>
                                    "No matching assignees"
                                  }
                                  menuPortalTarget={
                                    typeof document !== "undefined"
                                      ? document.body
                                      : null
                                  }
                                  menuPosition="fixed"
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 1200,
                                    }),
                                  }}
                                />
                              </span>
                            </label>
                            </div>


                            <label>
                              <b>
                                Query Details <em>*</em>
                              </b>
                              <span className="checklist-review__query-editor">
                                <textarea
                                  rows="6"
                                  maxLength="2000"
                                  value={newQuery.details}
                                  onChange={(event) =>
                                    setNewQuery((current) => ({
                                      ...current,
                                      details: event.target.value,
                                    }))
                                  }
                                  placeholder="Describe the query, including background, specific information required, and any relevant context..."
                                />
                              </span>
                              <small className="checklist-review__query-counter">
                                {newQuery.details.length}/2000
                              </small>
                            </label>
                            <section className="checklist-review__query-attachments">
                              <header>
                                <span>
                                  <i className="bx bx-paperclip" />
                                  <b>Attachments</b>
                                </span>
                                <small>
                                  {selectedQueryAttachments.length +
                                    queryAttachmentFiles.length}{" "}
                                  file
                                  {selectedQueryAttachments.length +
                                    queryAttachmentFiles.length ===
                                  1
                                    ? ""
                                    : "s"}
                                </small>
                              </header>
                              <label
                                className={`checklist-review__query-upload ${
                                  querySaving ? "is-disabled" : ""
                                }`}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  addQueryAttachments(
                                    Array.from(event.dataTransfer.files || [])
                                  );
                                }}
                              >
                                <input
                                  type="file"
                                  hidden
                                  multiple
                                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg"
                                  disabled={querySaving}
                                  onChange={stageQueryAttachments}
                                />
                                <i className="bx bx-cloud-upload" />
                                <b>
                                  Drag and drop files here, or click to browse
                                </b>
                                <small>
                                  Supported formats: PDF, DOC, DOCX, XLS, XLSX,
                                  PPT, PNG, JPG (Max 25 MB each)
                                </small>
                              </label>
                              {(selectedQueryAttachments.length > 0 ||
                                queryAttachmentFiles.length > 0) && (
                                <div>
                                  {selectedQueryAttachments.map((file) => (
                                    <article key={file.id}>
                                      <i
                                        className={`bx ${getDocumentIcon(
                                          file.fileName || file.name
                                        )}`}
                                      />
                                      <span>
                                        <b>{file.fileName || file.name}</b>
                                        <small>
                                          {Math.max(
                                            1,
                                            Math.round(
                                              Number(file.sizeBytes || 0) / 1024
                                            )
                                          )}{" "}
                                          KB
                                        </small>
                                      </span>
                                    </article>
                                  ))}
                                  {queryAttachmentFiles.map((file) => (
                                    <article key={`${file.name}-${file.size}`}>
                                      <i
                                        className={`bx ${getDocumentIcon(
                                          file.name
                                        )}`}
                                      />
                                      <span>
                                        <b>{file.name}</b>
                                        <small>
                                          {Math.max(
                                            1,
                                            Math.round(
                                              Number(file.size || 0) / 1024
                                            )
                                          )}{" "}
                                          KB · pending
                                        </small>
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setQueryAttachmentFiles((items) =>
                                            items.filter(
                                              (item) => item !== file
                                            )
                                          )
                                        }
                                        aria-label={`Remove ${file.name}`}
                                      >
                                        <i className="bx bx-x" />
                                      </button>
                                    </article>
                                  ))}
                                </div>
                              )}
                            </section>
                            <footer className="checklist-review__query-form-footer">
                              {queryMode === "create" ? (
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={newQuery.notifyAssigneeByEmail}
                                    onChange={(event) =>
                                      setNewQuery((current) => ({
                                        ...current,
                                        notifyAssigneeByEmail:
                                          event.target.checked,
                                      }))
                                    }
                                  />{" "}
                                  Notify assignee by email
                                </label>
                              ) : (
                                <span />
                              )}
                              <span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedQuery(null);
                                    setQueryMode(null);
                                    setQueryAttachmentFiles([]);
                                  }}
                                  disabled={querySaving}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  disabled={
                                    querySaving ||
                                    !newQuery.assigneeId ||
                                    !newQuery.details.trim()
                                  }
                                  onClick={saveQuery}
                                >
                                  <i
                                    className={`bx ${
                                      querySaving
                                        ? "bx-loader-alt bx-spin"
                                        : "bx-send"
                                    }`}
                                  />{" "}
                                  {querySaving
                                    ? "Saving..."
                                    : queryMode === "edit"
                                    ? "Save Changes"
                                    : "Create Query"}
                                </button>
                              </span>
                            </footer>
                          </div>
                          <aside className="checklist-review__query-create-rail">
                            <section>
                              <header>
                                <i className="bx bx-info-circle" />
                                <b>Query Context</b>
                              </header>
                              <dl>
                                <div>
                                  <dt>
                                    <i className="bx bx-briefcase" /> Engagement
                                  </dt>
                                  <dd>{engagement}</dd>
                                </div>
                                <div>
                                  <dt>
                                    <i className="bx bx-map" /> Inspection
                                  </dt>
                                  <dd>{reviewIdentifier}</dd>
                                </div>
                                <div>
                                  <dt>
                                    <i className="bx bx-buildings" /> Client
                                  </dt>
                                  <dd>{client}</dd>
                                </div>
                                <div>
                                  <dt>
                                    <i className="bx bx-list-check" /> Checklist
                                  </dt>
                                  <dd>{selectedQuestion.section}</dd>
                                </div>
                                <div>
                                  <dt>
                                    <i className="bx bx-clipboard" /> Checklist
                                    Item
                                  </dt>
                                  <dd>
                                    {selectedQuestion.code} -{" "}
                                    {selectedQuestion.question}
                                  </dd>
                                </div>
                                <div>
                                  <dt>
                                    <i className="bx bx-check-circle" /> Status
                                  </dt>
                                  <dd>
                                    <em>In Progress</em>
                                  </dd>
                                </div>
                              </dl>
                            </section>
                          </aside>
                        </div>
                      </section>
                    ) : (
                      <section className="checklist-review__queries-panel" ref={queriesPanelRef}>
                        {canRaiseFinding && newQueryFormOpen && <section ref={newQueryFormRef} id="new-question-query-form" className="question-entry-form question-query-create"><h3>Add a new query</h3><label className="question-query-create__details"><span className="visually-hidden">Query details</span><textarea autoFocus aria-label="New query details" placeholder="Type your query here…" value={newQuery.details} disabled={querySaving} maxLength={2000} onChange={(event) => setNewQuery((current) => ({ ...current, details: event.target.value }))} /></label><label>Assign to<select aria-label="Assign query to" disabled={querySaving} value={newQuery.assigneeId || ""} onChange={(event) => setNewQuery((current) => ({ ...current, assigneeId: event.target.value }))}><option value="">Select an engagement team member</option>{queryAssignees.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}</select></label><footer><label className="is-link"><i className="bx bx-paperclip" /> Attach file (optional)<input type="file" hidden multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg" onChange={stageQueryAttachments} disabled={querySaving} /></label><small>{newQuery.details.length}/2000</small><button type="button" disabled={querySaving || !newQuery.details.trim() || !newQuery.assigneeId} onClick={saveQuery}>{querySaving ? "Adding…" : "Add Query"}</button><button type="button" className="question-entry-cancel" disabled={querySaving} onClick={() => { setNewQueryFormOpen(false); setNewQuery(emptyQuery()); setQueryAttachmentFiles([]); }}>Cancel</button></footer>{queryAttachmentFiles.map((file) => <small key={`${file.name}-${file.size}`}>{file.name}</small>)}</section>}


                        <div className="checklist-review__question-query-toolbar">


                          {queriesRefreshError && <p className="question-query-refresh-error" role="alert">{queriesRefreshError} <button type="button" onClick={refreshQueriesSection} disabled={queriesRefreshing}>Retry</button></p>}
                          <nav className="checklist-review__query-quick-filters question-status-filters" aria-label="Filter question queries by status">
                            {queryQuickFilterOptions.map((status) => (
                              <button type="button" key={status} className={queryStatusFilter === status ? "active" : ""} aria-pressed={queryStatusFilter === status} onClick={() => setQueryStatusFilter(status)}>
                                {status} <b>{status === "All" ? createdQueries.length : queryStageCounts[status] || 0}</b>
                              </button>
                            ))}
                          </nav>
                          <div className="question-query-tools">{canRaiseFinding && !newQueryFormOpen && <button type="button" className="question-query-refresh piers-refresh-button" disabled={querySaving} aria-expanded={newQueryFormOpen} aria-controls="new-question-query-form" onClick={() => { if (newQueryFormOpen) scrollToNewQueryForm(); else setNewQueryFormOpen(true); }}><i className="bx bx-plus" aria-hidden="true" /> New Query</button>}<button type="button" className="question-query-refresh piers-refresh-button" disabled={queriesRefreshing || querySaving} onClick={refreshQueriesSection}><i className={`bx bx-refresh ${queriesRefreshing ? "bx-spin" : ""}`} aria-hidden="true" /> {queriesRefreshing ? "Refreshing…" : "Refresh"}</button><label className="question-sort-control">Sort by: <select aria-label="Sort queries" value={querySort} onChange={(event) => setQuerySort(event.target.value)}><option value="oldest">Oldest first</option><option value="newest">Newest first</option></select></label></div>
                        </div>
                        <ul className="checklist-review__question-record-list" aria-label="Queries for this question">
                          {createdQueries.slice().sort((a, b) => querySort === "newest" ? new Date(b.createdOn) - new Date(a.createdOn) : new Date(a.createdOn) - new Date(b.createdOn)).map((query) => <ChecklistQueryThread readOnly={readOnly} initiallyOpen={Number(query.queryId || query.id) === Number(initialQueryId)} key={query.queryId || query.id}  query={query} inspectionId={inspectionId} sectionRefreshing={queriesRefreshing} onDelete={canRaiseFinding ? requestDeleteQuery : undefined} deleting={querySaving} onRefresh={refreshQueryConversation} onChanged={refreshQueryConversation} onDownload={downloadWorkflowQueryAttachment} />)}
                          {!visibleQueries.length && <li className="checklist-review__record-empty">{createdQueries.length ? "No queries match your search or filters." : "No queries have been raised for this question."}</li>}
                        </ul>
                      </section>
                    ))}
                  {panelDrawer === "findings" &&
                    (
<ObservationsPanel inlineEntry onRefreshFindings={async () => {
  const response = await getInspectionFindings(inspectionId, selectedQuestion.id);
  if (!response?.status) throw new Error(response?.message || "Unable to refresh findings.");
  setFindings((items) => [...items.filter((item) => String(item.questionId) !== String(selectedQuestion.id)), ...(response.data || [])]);
}} key={`${selectedQuestion.id}:${observationsRefresh}`} inspectionId={inspectionId} questionId={selectedQuestion.id} canEdit={canRaiseFinding} onCount={setObservationCount} createRequest={observationCreate} onConvert={(observation) => { openFindingForm(); setNewFinding((current) => ({ ...current, description: observation.description, observationId: observation.id })); }}>
{({ observations, onReviewObservation, controls, sort, refreshing, onDeleteObservation }) => (
                      <FindingsTab
                        controls={controls}
                        sort={sort}
                        onDeleteObservation={onDeleteObservation}
                        findings={questionFindings}
                        compact
                        hideTitle
                        showFilters={false}
                        alwaysShowTable
                        hideDateColumns
                        actionOpen={findingActionOpen}
                        onToggleAction={setFindingActionOpen}
                        onOpen={openFindingWorkspace}
                        onEdit={readOnly ? undefined : openFindingForEdit}
                        onDelete={canRaiseFinding ? deleteFinding : undefined}
                        createLabel="Add Observation / Finding"
                        onCreate={undefined}
                        saving={findingSaving || refreshing}
                        observations={observations}
                        canManageObservations={canRaiseFinding}
                        onReviewObservation={onReviewObservation}
                      />
)}
</ObservationsPanel>
                    )}
                  {panelDrawer === "history" && (
                    <section className="checklist-review__context-panel checklist-review__history-panel">
                      <header onClick={() => setHistoryOpen((value) => !value)}>
                        <span>
                          <i className="bx bx-history" />{" "}
                          <b>
                            History <em>({activities.length})</em>
                          </b>
                        </span>
                        <i
                          className={`bx bx-chevron-${
                            historyOpen ? "up" : "down"
                          }`}
                        />
                      </header>
                      {historyOpen && (
                        <div className="checklist-review__history-list">
                          {activities.length ? (
                            activities.map((activity) => (
                              <article key={activity.id}>
                                <b>{activity.activityNote}</b>
                                <small>
                                  {activity.userProfile?.fullName ||
                                    "Engagement team"}
                                  {activity.activityDate
                                    ? ` · ${new Date(
                                        activity.activityDate
                                      ).toLocaleString("en-GB")}`
                                    : ""}
                                </small>
                              </article>
                            ))
                          ) : (
                            <p>
                              No activity has been recorded for this review.
                            </p>
                          )}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              </aside>
            );
            if (isHistoryDrawer)
              return (
                <>
                  {!drawerPinned && (
                    <div
                      className="checklist-review__drawer-backdrop checklist-review__context-drawer-backdrop"
                      aria-hidden="true"
                    />
                  )}
                  {panel}
                </>
              );
            const host = panelDrawer === "queries" ? queryHost : findingHost;
            return host ? createPortal(panel, host, panelDrawer) : null;
          })}
      </div>
      <input ref={uploadRef} type="file" hidden onChange={addFile} />
      <Modal isOpen={!readOnly && Boolean(queryToDelete)} toggle={() => { if (!querySaving) setQueryToDelete(null); }} centered labelledBy="delete-query-title">
        <ModalBody>
          <h2 id="delete-query-title">Delete query?</h2>
          <p>Delete {queryToDelete?.taskNumber || queryToDelete?.queryNumber || `RQ-${queryToDelete?.queryId}`}? The linked task will no longer appear in active task lists.</p>
          {queryDeleteError && <p role="alert" className="text-danger">{queryDeleteError}</p>}
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-secondary" disabled={querySaving} onClick={() => setQueryToDelete(null)}>Cancel</button>
            <button type="button" className="btn btn-danger" disabled={querySaving} onClick={deleteQuery}>{querySaving ? "Deleting…" : "Delete Query"}</button>
          </div>
        </ModalBody>
      </Modal>
      <Modal isOpen={!readOnly && submittedInfoOpen} toggle={() => setSubmittedInfoOpen(false)} centered>
        <ModalBody className="checklist-review__submitted-info">
          <h2>This checklist is submitted</h2>
          <p>This checklist has already been submitted for review.</p>
          <div>
            <button type="button" onClick={() => setSubmittedInfoOpen(false)}>Close</button>
            <button type="button" disabled={Boolean(submissionStage)} onClick={async () => {
              setSubmissionStage("generating");
              try {
                const response = await completeInspectionReviewWorkflowTask(inspectionId, workflowTask.id);
                if (!response?.status) throw new Error(response?.message || "Unable to generate checklist report.");
                SuccessNotify(response.data?.generatedReports ? "Checklist report generated. View it in Reports." : "Checklist report is already available in Reports.");
                setSubmittedInfoOpen(false);
              } catch (error) { ErrorNotify(error.message); }
              finally { setSubmissionStage(null); }
            }}>{submissionStage ? "Generating…" : "Generate Checklist Report"}</button>
            <button type="button" className="is-primary" onClick={() => { setSubmittedInfoOpen(false); allChecklistsAction?.(); }}>Back to Checklists</button>
          </div>
        </ModalBody>
      </Modal>
      {engagementDetailOpen && (
        <>
          <button type="button" className="checklist-review__drawer-backdrop checklist-review__context-drawer-backdrop"
            aria-label="Close engagement details" onClick={() => setEngagementDetailOpen(false)} />
          <aside className="checklist-review__drawer checklist-review__context-drawer checklist-review__engagement-details-drawer" aria-label="Engagement details drawer">
            <header className="checklist-review__drawer-header">
              <span><i className="bx bx-briefcase" aria-hidden="true" /><b>Engagement Details</b></span>
              <button type="button" className="checklist-review__drawer-close" style={{ marginLeft: "auto" }}
                aria-label="Close engagement details" onClick={() => setEngagementDetailOpen(false)}>
                <i className="bx bx-x" aria-hidden="true" /> Close
              </button>
            </header>
            {engagementBasicsLoading && <p role="status">Loading engagement details…</p>}
            {engagementBasicsError && <p role="alert">Unable to load full engagement details. Available review details are shown below.</p>}
            <dl className="checklist-review__engagement-details-fields">
              {[
                ["Engagement Name", engagement],
                ["Engagement Number", basicEngagement?.engagementNo],
                ["Inspection Cycle", engagementCycle
                  ? [engagementCycle.projectNo, engagementCycle.title || engagementCycle.name].filter(Boolean).join(" · ")
                  : engagementCycleError ? "Unable to load inspection cycle" : engagementCycleId ? "Loading…" : "Not assigned"],
                ["Work Type", basicEngagement?.workType?.name],
                ["Client", client],
                ["Client Type", basicEngagement?.clientType?.name],
                ["Industry Type", basicEngagement?.industryType?.name],
              ].map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{value || "—"}</dd></div>
              ))}
            </dl>
          </aside>
        </>
      )}
      <Modal isOpen={recordChoiceOpen} toggle={() => setRecordChoiceOpen(false)} centered className="checklist-record-choice" labelledBy="record-choice-title">
        <header className="checklist-record-choice__header">
          <div><i className="bx bx-plus-circle" aria-hidden="true" /><h2 id="record-choice-title">Add Observation / Finding</h2></div>
          <button type="button" aria-label="Close dialog" onClick={() => setRecordChoiceOpen(false)}><i className="bx bx-x" aria-hidden="true" /></button>
        </header>
        <ModalBody>
          <p>Choose the type of record you want to add for this question.</p>
          <div className="checklist-record-choice__options">
            <button type="button" onClick={() => { setRecordChoiceOpen(false); selectQuestionTab("findings"); setObservationCreate((value) => value + 1); }}>
              <i className="bx bx-note" aria-hidden="true" /><span><strong>Add Observation</strong><small>Record an observation for assessment and confirmation.</small></span><i className="bx bx-chevron-right" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => { setRecordChoiceOpen(false); openFindingForm(); }}>
              <i className="bx bx-flag" aria-hidden="true" /><span><strong>Add Finding</strong><small>Document a finding and its required follow-up.</small></span><i className="bx bx-chevron-right" aria-hidden="true" />
            </button>
          </div>
        </ModalBody>
        <footer className="checklist-record-choice__footer"><button type="button" onClick={() => setRecordChoiceOpen(false)}>Cancel</button></footer>
      </Modal>
      <FindingEditorModal
        mode={findingMode}
        findingNumber={selectedFinding?.findingNumber}
        engagement={engagement}
        checklist={selectedQuestion.section}
        question={`${selectedQuestion.code} - ${selectedQuestion.question}`}
        value={newFinding}
        onChange={setNewFinding}
        configuration={findingConfiguration}
        requiredFields={requiredFindingFields}
        evidenceFiles={findingEvidenceFiles}
        assignees={queryAssignees}
        errors={findingErrors}
        onErrors={setFindingErrors}
        onBrowseFiles={() => uploadRef.current?.click()}
        onRemoveEvidence={(fileId) =>
          setNewFinding((current) => ({
            ...current,
            evidenceIds: current.evidenceIds.filter(
              (id) => String(id) !== String(fileId)
            ),
          }))
        }
        onClose={() => {
          setFindingMode(false);
          setSelectedFinding(null);
          setFindingErrors({});
        }}
        onSave={saveFinding}
        saving={findingSaving}
        evidenceUploading={evidenceUploading}
      />
      <FindingWorkspaceModal
        readOnly={readOnly}
        finding={findingWorkspace}
        assigneeName={
          findingWorkspace?.responseRequestedFromUserName ||
          queryAssignees.find(
            (user) =>
              String(user.id) ===
              String(findingWorkspace?.responseRequestedFromUserId)
          )?.name
        }
        onClose={() => !findingSaving && setFindingWorkspace(null)}
        onEdit={readOnly ? undefined : () => {
          const finding = findingWorkspace;
          setFindingWorkspace(null);
          openFindingForEdit(finding);
        }}
        onPostComment={postFindingComment}
        onTransition={transitionFinding}
        onSubmitResponse={() => {
          const finding = findingWorkspace;
          setFindingWorkspace(null);
          openWorkflowResponse("finding", finding);
        }}
        onDownload={downloadEvidenceFile}
        saving={findingSaving}
      />
      <WorkflowResponseDrawer
        inspectionId={inspectionId}
        onReviewPosted={async () => { const id = workflowResponse?.item?.queryId; if (id) { const item = await refreshQueryConversation(id); setWorkflowResponse((current) => current ? { ...current, item: { ...current.item, ...item } } : current); } }}
        presentation={workflowResponse?.type === "query" ? "modal" : "drawer"}
        response={workflowResponse}
        value={workflowResponseText}
        onChange={setWorkflowResponseText}
        onClose={closeWorkflowResponse}
        onSubmit={saveWorkflowResponse}
        onResolve={
          workflowResponse?.type === "query" &&
          workflowResponse.item?.canClose && workflowResponse.item?.resolutionEligible &&
          queryStage(workflowResponse.item.status) === "Open"
            ? resolveWorkflowQuery
            : null
        }
        attachments={workflowResponseAttachments}
        onUpload={
          workflowResponse?.type === "query"
            ? stageQueryResponseAttachment
            : workflowResponse?.type === "finding"
            ? uploadWorkflowResponseAttachment
            : null
        }
        onDownload={
          workflowResponse?.type === "query"
            ? downloadWorkflowQueryAttachment
            : downloadEvidenceFile
        }
        uploading={evidenceUploading}
        saving={workflowResponseSaving}
      />
    </div>
    </FullScreenPageLayout></ReviewActivityProvider>
  );
};

export default InspectionChecklistReview;
